<template>
  <div class="page-col page-main nfe-cadastro bg-light">
    <HeaderPadrao
      icon="fa fa-file-text-o"
      :titulo="'Cadastro NF-e'"
      :subtitulo="subtitulo"
    >
      <template #subtitulo>
        {{ subtitulo }}
        <b-badge 
          v-if="nfe.OP_STATUS !== null" 
          pill 
          :class="['ml-2 px-3 py-1 text-uppercase font-weight-bold shadow-sm border-0', 'badge-status-nfe-' + nfe.OP_STATUS]"
          :style="statusBadgeStyle"
        >
          <i :class="statusIcon" class="mr-1"></i>
          {{ statusDescricao }}
        </b-badge>
      </template>
      <template #acoes>
        <b-button v-if="podeEditar" variant="link" class="btn-glass btn-glass-success mr-2" @click="salvar">
          <i class="fa fa-save mr-1"></i> Salvar
        </b-button>

        <b-button v-if="(nfeAssinada || nfeRejeitada)" variant="link" class="btn-glass btn-glass-warning mr-2" @click="habilitarEdicao">
          <i class="fa fa-edit mr-1"></i> Editar
        </b-button>

        <b-button variant="link" class="btn-glass btn-glass-primary mr-2" @click="transmitir" :disabled="!(podeEditar || nfeAssinada)">
          <i class="fa fa-paper-plane mr-1"></i> Transmitir
        </b-button>

        <b-button variant="link" class="btn-glass btn-glass-info mr-2" @click="consultarSefaz" :disabled="consultandoStatusServico">
          <i :class="consultandoStatusServico ? 'fa fa-spinner fa-spin mr-1' : 'fa fa-refresh mr-1'"></i> Status Serviço
        </b-button>
        <b-button variant="link" class="btn-glass btn-glass-secondary mr-2" @click="imprimirDanfe" :disabled="!nfeAutorizada">
          <i class="fa fa-print mr-1"></i> Imprimir DANFE
        </b-button>
        <b-button variant="link" class="btn-glass btn-glass-info mr-2" @click="enviarEmail" :disabled="!nfeAutorizada">
          <i class="fa fa-envelope mr-1"></i> Enviar Email
        </b-button>
        <b-button variant="link" class="btn-glass btn-glass-secondary mr-2" @click="exportarXML" :disabled="!nfeAutorizada">
          <i class="fa fa-download mr-1"></i> Exportar XML
        </b-button>

        <b-button variant="light" pill class="ml-2 font-weight-bold shadow-sm" @click="voltar">
          <i class="fa fa-arrow-left mr-1"></i> Voltar
        </b-button>
      </template>
    </HeaderPadrao>

    <!-- Summary Bar -->
    <div class="summary-bar shadow-sm mb-4 mx-0 rounded-pill bg-white px-4 py-2 d-flex justify-content-between align-items-center border">
      <div class="d-flex align-items-center py-1">
        <div class="bg-soft-primary p-2 rounded-circle mr-3">
          <i class="fa fa-user text-primary"></i>
        </div>
        <div>
          <small class="text-muted d-block font-weight-bold text-uppercase letter-spacing-1" style="font-size: 0.65rem;">Destinatário / Cliente</small>
          <span class="h6 mb-0 font-weight-bold text-dark">{{ nfe.TX_RAZAO_SOCIAL_DEST || 'CONSUMIDOR NÃO IDENTIFICADO' }}</span>
        </div>
      </div>

      <div class="d-flex align-items-center py-1">
        <div class="text-right mr-3">
          <small class="text-muted d-block font-weight-bold text-uppercase letter-spacing-1" style="font-size: 0.65rem;">Chave de Acesso</small>
          <span class="mb-0 font-weight-bold text-secondary" style="font-family: 'Courier New', Courier, monospace; letter-spacing: 1px;">
            {{ nfe.TX_CHAVE_ACESSO || 'NF-E AINDA NÃO TRANSMITIDA' }}
          </span>
        </div>
        <div class="bg-soft-info p-2 rounded-circle">
          <i class="fa fa-barcode text-info"></i>
        </div>
      </div>
    </div>

    <div class="pb-4">
      <b-row>
        <b-col cols="9">
          <b-tabs v-model="activeTab" pills nav-class="nav-pills-premium mb-4" content-class="premium-tab-content">
  
            <b-tab title="Dados da NF-e" active>
              <b-overlay :show="loadingTabs.dados" rounded="lg" opacity="0.6">
                <nfe-aba-dados
                  :nfe="nfe"
                  :is-loading-from-backend="isLoadingFromBackend"
                  :pode-editar="podeEditar"
                  :key="'dados-' + abasKey"
                  @tipo-movimentacao-alterada="onTipoMovimentacaoAlterada"
                />
              </b-overlay>
            </b-tab>

            <b-tab title="NF-e Referenciada">
              <b-overlay :show="loadingTabs.referenciadas" rounded="lg" opacity="0.6">
                <nfe-aba-nfe-referenciada :nfe="nfe" :pode-editar="podeEditar" :key="'referenciadas-' + abasKey" />
              </b-overlay>
            </b-tab>

            <b-tab title="Emitente">
              <b-overlay :show="loadingTabs.emitente" rounded="lg" opacity="0.6">
                <nfe-aba-emitente :nfe="nfe" :pode-editar="podeEditar" :key="'emitente-' + abasKey" />
              </b-overlay>
            </b-tab>

            <b-tab title="Destinatário/Remetente">
              <b-overlay :show="loadingTabs.dest" rounded="lg" opacity="0.6">
                <nfe-aba-destinatario :nfe="nfe" :is-loading-from-backend="isLoadingFromBackend" :pode-editar="podeEditar" :key="'dest-' + abasKey" @changed="onDestinatarioAlterado" />
              </b-overlay>
            </b-tab>

            <b-tab title="Produtos & Serviços">
              <b-overlay :show="loadingTabs.produtos" rounded="lg" opacity="0.6">
                <nfe-aba-produtos
                  :nfe="nfe"
                  :items="itensNfe"
                  :loading="itemModalBusy || loadingTabs.produtos"
                  :pode-editar="podeEditar"
                  :key="'produtos-' + abasKey"
                  @add-item="abrirModalItemNovo"
                  @edit-item="abrirModalItemEditar"
                  @remove-item="removerItemNfe"
                />
              </b-overlay>
            </b-tab>


            <b-tab title="Transporte">
              <b-overlay :show="loadingTabs.transporte" rounded="lg" opacity="0.6">
                <nfe-aba-transporte :nfe="nfe" :is-loading-from-backend="isLoadingFromBackend" :pode-editar="podeEditar" :key="'transporte-' + abasKey" />
              </b-overlay>
            </b-tab>
          

            <b-tab title="Cobrança (Parcelas)">
              <b-overlay :show="loadingTabs.cobranca" rounded="lg" opacity="0.6">
                <nfe-aba-cobranca :nfe="nfe" :pode-editar="podeEditar" :key="'cobranca-' + abasKey" @nfe-atualizada="onNfeAtualizada" />
              </b-overlay>
            </b-tab>

            <b-tab title="Informações Adicionais">
              <b-overlay :show="loadingTabs.info" rounded="lg" opacity="0.6">
                <nfe-aba-info-adic :nfe="nfe" :pode-editar="podeEditar" :key="'info-' + abasKey" />
              </b-overlay>
            </b-tab>

            <b-tab title="Eventos">
              <b-overlay :show="loadingTabs.eventos" rounded="lg" opacity="0.6">
                <b-card class="border-0 shadow-sm rounded-lg" body-class="p-4">
                  <div class="d-flex justify-content-between align-items-center mb-4">
                    <h5 class="mb-0 font-weight-bold text-dark">Histórico de Eventos</h5>
                    <div>
                      <b-button
                        size="sm"
                        variant="warning"
                        :disabled="!nfeAutorizada"
                        @click="abrirModalCartaCorrecaoAba"
                        title="Gerar Carta de Correção"
                        class="mr-2 rounded-pill px-3 shadow-sm text-white font-weight-bold"
                      >
                        <i class="fa fa-pencil mr-1"></i> Carta de Correção
                      </b-button>
                      <b-button
                        size="sm"
                        variant="danger"
                        :disabled="!nfeAutorizada"
                        @click="abrirModalCancelarAba"
                        title="Cancelar NF-e"
                        class="rounded-pill px-3 shadow-sm font-weight-bold"
                      >
                        <i class="fa fa-ban mr-1"></i> Cancelar NF-e
                      </b-button>
                    </div>
                  </div>
                  <b-table
                    hover
                    responsive
                    :items="eventos"
                    :fields="eventosFields"
                    :busy="loadingEventos"
                    show-empty
                    empty-text="Não há eventos registrados para esta nota."
                    class="premium-table"
                  >
                    <template #cell(OP_TIPO)="data">
                      <b-badge pill :class="getEventoTipoBadgePremium(data.value)">{{ getEventoTipoTexto(data.value) }}</b-badge>
                    </template>
                    
                    <template #cell(DATA)="data">
                      <span class="text-muted font-weight-bold">{{ formatarData(data.value) }}</span>
                    </template>

                    <template #cell(acoes)="data">
                      <b-button
                        v-if="data.item.OP_TIPO === 'T'"
                        size="sm"
                        variant="outline-info"
                        @click="imprimirCartaCorrecao(data.item.ID)"
                        title="Imprimir Carta de Correção"
                        class="mr-1 btn-icon-only rounded-circle"
                      >
                        <i class="fa fa-print"></i>
                      </b-button>
                      <b-button
                        size="sm"
                        variant="outline-secondary"
                        @click="verDetalhesEvento(data.item)"
                        title="Detalhes do Evento"
                        class="btn-icon-only rounded-circle"
                      >
                        <i class="fa fa-eye"></i>
                      </b-button>
                    </template>
                  </b-table>
                </b-card>
              </b-overlay>
            </b-tab>
          
          </b-tabs>
        </b-col>

        <b-col cols="3">
          <nfe-painel-totais class="mt-5" :nfe="nfe" :key="totaisKey" @ver-detalhes="abrirModalImpostos" />
        </b-col>
      </b-row>
    </div>

    <b-modal
      ref="modalItemNfe"
      id="modal-item-nfe"
      size="xl"
      hide-header
      hide-footer
      body-class="p-0 overflow-hidden"
      content-class="border-0 shadow-lg"
      @shown="onItemModalShown"
      @hidden="onItemModalHidden"
    >
      <div class="modal-header-premium p-3 d-flex align-items-center bg-dark" style="background: linear-gradient(135deg, #2c3e50 0%, #000000 100%) !important;">
        <div class="header-icon-wrapper-modal mr-3 bg-white-100">
          <i class="fa fa-cube text-warning"></i>
        </div>
        <div>
          <h5 class="mb-0 font-weight-bold text-white">{{ itemModalEdicao ? 'Editar Item da NF-e' : 'Adicionar Novo Item' }}</h5>
          <small class="text-white opacity-75">Use um único objeto do item, no padrão das telas internas de venda.</small>
        </div>
        <b-button variant="link" class="ml-auto text-white p-0 opacity-75" @click="$refs.modalItemNfe.hide()">
          <i class="fa fa-times fa-lg"></i>
        </b-button>
      </div>

      <b-overlay :show="itemModalBusy" opacity="0.6" rounded="sm">
        <div class="p-4 bg-white modal-scrollable-content">
          <fieldset :disabled="!podeEditar" class="border-0 p-0 m-0">
            <div class="modal-info-bar mb-4 bg-light border shadow-sm rounded overflow-hidden">
              <b-row align-v="center" class="m-0 text-center">
                <b-col md="8" class="p-3 text-left">
                  <small class="modal-label-premium">Produto Selecionado</small>
                  <h6 class="mb-0 font-weight-bold text-primary text-truncate">
                    <template v-if="itemModalLocal.idProduto">
                      <i class="fa fa-barcode mr-1"></i>{{ itemModalLocal.idProduto }} - {{ itemProdutoNomeCache }}
                    </template>
                    <template v-else>
                      Aguardando...
                    </template>
                  </h6>
                </b-col>
                <b-col md="4" class="border-left p-3 bg-white">
                  <small class="modal-label-premium">Total do Item</small>
                  <h5 class="mb-0 font-weight-bold text-success">{{ itemValorTotalBruto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</h5>
                </b-col>
              </b-row>
            </div>

            <b-row>
              <b-col md="9">
                <b-tabs v-model="itemActiveTab" nav-class="nav-pills-premium mb-3" content-class="mt-3">
                  <b-tab active>
                    <template #title>
                      <i class="fa fa-info-circle mr-1"></i> Dados Gerais
                    </template>

                    <div class="premium-card-section mb-4">
                      <div class="modal-section-title mb-3">
                        <i class="fa fa-shopping-cart mr-2 text-primary"></i> Operação e Produto
                      </div>

                      <b-row>
                        <b-col md="12" class="mb-3">
                          <b-form-group label="Produto" label-class="modal-label-premium">
                            <produto-pesquisa
                              ref="produtoPesquisaNfe"
                              :idInicial="itemModalLocal.idProduto ? Number(itemModalLocal.idProduto) : undefined"
                              @retornaId="onItemProdutoSelected"
                              @retornaNome="onItemProdutoNome"
                              place="Busque pelo código ou nome do produto..."
                              :arredondado="true"
                              :desabilitarInput="itemModalEdicao"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col md="12">
                          <b-form-group label="* CFOP (Operação Fiscal)" label-class="modal-label-premium font-weight-normal">
                            <CFOPDropDown v-model="itemModalLocal.cfopId" :disabled="itemModalBusy" input-class="font-weight-normal" />
                          </b-form-group>
                        </b-col>
                      </b-row>
                    </div>

                    <div class="premium-card-section bg-soft-primary p-3 rounded-lg border-primary-light mb-4">
                      <div class="modal-section-title mb-3">
                        <i class="fa fa-money mr-2 text-success"></i> Quantidades e Valores
                      </div>
                      <b-row>
                        <b-col md="4">
                          <b-form-group label="Quantidade" label-class="modal-label-premium">
                            <vue-autonumeric
                              v-model="itemModalLocal.quantidadeComercial"
                              :options="itemAutonumericOptions.quantidade"
                              class="form-control rounded-pill shadow-xs text-right font-weight-bold"
                              @input="calcularTotalItemModal"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col md="4">
                          <b-form-group label="Peças / Volumes" label-class="modal-label-premium">
                            <vue-autonumeric
                              v-model="itemModalLocal.pecas"
                              :options="itemAutonumericOptions.pecas"
                              class="form-control rounded-pill shadow-xs text-right"
                              @input="calcularTotalItemModal"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col md="4">
                          <b-form-group label="Preço Unitário" label-class="modal-label-premium">
                            <vue-autonumeric
                              v-model="itemModalLocal.valorUnitarioComercial"
                              :options="itemAutonumericOptions.valor"
                              class="form-control rounded-pill shadow-xs font-weight-bold text-primary text-right"
                              @input="calcularTotalItemModal"
                            />
                          </b-form-group>
                        </b-col>
                      </b-row>

                      <b-row class="mt-2 align-items-center">
                        <b-col md="3">
                          <b-form-group label="Unidade" label-class="modal-label-premium">
                            <b-form-select v-model="itemModalLocal.idUnidadeComercial" :options="itemUnidadeOptions" class="rounded-pill shadow-xs" />
                          </b-form-group>
                        </b-col>
                        <b-col md="4">
                          <b-form-group label="Desconto Total" label-class="modal-label-premium">
                            <vue-autonumeric
                              v-model="itemModalLocal.valorTotalDesconto"
                              :options="itemAutonumericOptions.valor"
                              class="form-control rounded-pill shadow-xs text-right text-danger"
                              @input="calcularTotalItemModal"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col md="5">
                          <b-form-group label="Total do Item (Bruto)" label-class="modal-label-premium">
                            <vue-autonumeric
                              :value="itemValorTotalBruto"
                              :options="itemAutonumericOptions.valor"
                              class="form-control rounded-pill shadow-xs bg-white font-weight-bold text-right"
                              readonly
                              disabled
                            />
                          </b-form-group>
                        </b-col>
                      </b-row>
                    </div>

                    <div class="premium-card-section mb-4">
                      <div class="modal-section-title mb-3">
                        <i class="fa fa-list-alt mr-2 text-info"></i> Especificações e Códigos
                      </div>
                      <b-row>
                        <b-col md="4">
                          <b-form-group label="EAN (Barras)" label-class="modal-label-premium">
                            <b-form-input v-model="itemModalLocal.ean" maxlength="13" class="rounded-pill shadow-xs" placeholder="GTIN/EAN" />
                          </b-form-group>
                        </b-col>
                        <b-col md="4">
                          <b-form-group label="NCM/SH" label-class="modal-label-premium">
                            <b-form-input v-model="itemModalLocal.ncm" maxlength="8" class="rounded-pill shadow-xs" placeholder="Nomenclatura Comum" />
                          </b-form-group>
                        </b-col>
                        <b-col md="4">
                          <b-form-group label="CEST" label-class="modal-label-premium">
                            <b-form-input v-model="itemModalLocal.cest" maxlength="7" class="rounded-pill shadow-xs" placeholder="Subst. Tributária" />
                          </b-form-group>
                        </b-col>
                      </b-row>
                    </div>
                  </b-tab>

                  <b-tab>
                    <template #title>
                      <i class="fa fa-percent mr-1"></i> IPI
                    </template>
                    <div class="premium-tab-container">
                      <nfe-produto-ipi v-model="itemModalLocal" />
                    </div>
                  </b-tab>

                  <b-tab>
                    <template #title>
                      <i class="fa fa-percent mr-1"></i> ICMS
                    </template>
                    <div class="premium-tab-container">
                      <nfe-produto-icms v-model="itemModalLocal" />
                    </div>
                  </b-tab>

                  <b-tab>
                    <template #title>
                      <i class="fa fa-percent mr-1"></i> PIS
                    </template>
                    <div class="premium-tab-container">
                      <nfe-produto-pis v-model="itemModalLocal" />
                    </div>
                  </b-tab>

                  <b-tab>
                    <template #title>
                      <i class="fa fa-percent mr-1"></i> COFINS
                    </template>
                    <div class="premium-tab-container">
                      <nfe-produto-cofins v-model="itemModalLocal" />
                    </div>
                  </b-tab>

                  <b-tab>
                    <template #title>
                      <i class="fa fa-gavel mr-1"></i> Reforma
                    </template>
                    <div class="p-0">
                      <div class="premium-card-section mb-4">
                        <div class="modal-section-title mb-4">
                          <i class="fa fa-university mr-2 text-primary"></i> Tributação IBS / CBS / IS
                        </div>

                        <b-row class="mb-4">
                          <b-col md="12">
                            <b-form-group label="Situação Tributária IBS/CBS" label-class="modal-label-premium">
                              <b-form-select
                                v-model="itemModalLocal.reforma.cst_ibscbs"
                                :options="itemCstIbscbsOptions"
                                class="rounded-pill shadow-xs custom-select-premium"
                              >
                                <template #first>
                                  <b-form-select-option :value="null">Selecione a situação tributária...</b-form-select-option>
                                </template>
                              </b-form-select>
                            </b-form-group>
                          </b-col>
                        </b-row>
                      </div>

                      <div class="premium-card-section bg-soft-info p-4 rounded-lg border-info-light shadow-sm">
                        <b-row>
                          <b-col md="4">
                            <b-form-group label="Alíquota IBS UF %" label-class="modal-label-premium">
                              <vue-autonumeric v-model="itemModalLocal.reforma.pibsuf" :options="itemAutonumericOptions.quantidade" class="form-control rounded-pill shadow-xs text-right bg-white" />
                            </b-form-group>
                          </b-col>
                          <b-col md="4">
                            <b-form-group label="Redução IBS UF %" label-class="modal-label-premium">
                              <vue-autonumeric v-model="itemModalLocal.reforma.predaliq_ibsuf" :options="itemAutonumericOptions.quantidade" class="form-control rounded-pill shadow-xs text-right bg-white" />
                            </b-form-group>
                          </b-col>
                          <b-col md="4">
                            <b-form-group label="Alíquota CBS %" label-class="modal-label-premium">
                              <vue-autonumeric v-model="itemModalLocal.reforma.pcbs" :options="itemAutonumericOptions.quantidade" class="form-control rounded-pill shadow-xs text-right bg-white" />
                            </b-form-group>
                          </b-col>
                        </b-row>

                        <b-row class="mt-3">
                          <b-col md="4">
                            <b-form-group label="Redução CBS %" label-class="modal-label-premium">
                              <vue-autonumeric v-model="itemModalLocal.reforma.predaliq_cbs" :options="itemAutonumericOptions.quantidade" class="form-control rounded-pill shadow-xs text-right bg-white" />
                            </b-form-group>
                          </b-col>
                          <b-col md="4">
                            <b-form-group label="Alíquota IS %" label-class="modal-label-premium">
                              <vue-autonumeric v-model="itemModalLocal.reforma.pis_seletivo" :options="itemAutonumericOptions.quantidade" class="form-control rounded-pill shadow-xs text-right bg-white" />
                            </b-form-group>
                          </b-col>
                        </b-row>
                      </div>
                    </div>
                  </b-tab>

                  <b-tab>
                    <template #title>
                      <i class="fa fa-ship mr-1"></i> Importação
                    </template>
                    <div class="premium-tab-container">
                      <nfe-produto-importacao v-model="itemModalLocal.importacao" />
                    </div>
                  </b-tab>
                </b-tabs>
              </b-col>

              <b-col md="3">
                <div class="fiscal-dashboard-item">
                  <div class="premium-summary-card mb-3 bg-light border-0 shadow-sm rounded-lg overflow-hidden">
                    <div class="card-header-mini bg-secondary text-white px-3 py-2 d-flex justify-content-between align-items-center">
                      <small class="font-weight-bold text-uppercase letter-spacing-1">Logística / Estoque</small>
                      <i class="fa fa-truck"></i>
                    </div>
                    <div class="p-3">
                      <b-row class="mb-2">
                        <b-col sm="12">
                          <label class="modal-label-premium small">Centro de Estoque Origem</label>
                          <b-form-select v-model="itemModalLocal.idCentroEstoque" :options="[]" class="rounded-pill shadow-xs border-0 bg-white" disabled />
                        </b-col>
                      </b-row>
                      <b-row>
                        <b-col sm="6">
                          <label class="modal-label-premium small">Disponível</label>
                          <div class="h5 mb-0 font-weight-bold text-dark text-truncate">0,000</div>
                        </b-col>
                        <b-col sm="6">
                          <label class="modal-label-premium small">Peças</label>
                          <div class="h5 mb-0 font-weight-bold text-dark">0</div>
                        </b-col>
                      </b-row>
                    </div>
                  </div>

                  <div class="mt-3 px-2">
                    <div class="custom-control custom-checkbox custom-control-premium d-flex align-items-center justify-content-center bg-light p-2 rounded shadow-xs border">
                      <b-form-checkbox v-model="itemModalLocal.compoeValorNfe" class="custom-control-premium m-0">
                        <span class="font-weight-bold text-dark small">Compõe valor total da NF-e</span>
                      </b-form-checkbox>
                    </div>
                  </div>

                  <div class="mt-3 p-2 bg-soft-warning rounded border-warning-light text-center">
                    <small class="text-warning-dark font-weight-bold">
                      <i class="fa fa-exclamation-triangle mr-1"></i> Confira os detalhes nas abas
                    </small>
                  </div>
                </div>
              </b-col>
            </b-row>
          </fieldset>
        </div>
      </b-overlay>

      <div class="modal-footer-premium p-3 bg-light border-top">
        <b-row class="w-100 m-0 align-items-center">
          <b-col class="p-0">
            <b-button variant="outline-secondary" pill class="px-4 font-weight-bold btn-premium-secondary" @click="$refs.modalItemNfe.hide()">
              <i class="fa fa-times mr-1"></i> {{ podeEditar ? 'CANCELAR' : 'FECHAR' }}
            </b-button>
          </b-col>
          <b-col v-if="podeEditar" class="p-0 text-right">
            <b-button variant="success" pill class="px-5 font-weight-bold shadow-lg btn-premium-success" @click="salvarItemModal">
              <i class="fa fa-check-circle mr-2"></i> {{ itemModalEdicao ? 'SALVAR ALTERAÇÕES' : 'CONFIRMAR E ADICIONAR' }}
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-modal>

    <!-- Modal de Erro Customizado -->
    <b-modal
      ref="modalErroTransmissao"
      title="Erro na Transmissão"
      size="lg"
      ok-only
      ok-variant="danger"
      ok-title="Fechar"
      header-class="modal-header-premium bg-gradient-danger text-white"
      body-class="p-4"
      footer-class="bg-light"
      centered
    >
      <div class="d-flex align-items-start">
        <div class="bg-light-danger p-3 rounded-circle mr-3">
          <i class="fa fa-exclamation-circle text-danger h4 mb-0"></i>
        </div>
        <div>
          <h5 class="text-danger font-weight-bold mb-2">Falha na Transmissão SEFAZ</h5>
          <div v-if="erroTransmissao.linhas.length > 1" class="text-muted">
            <ul class="pl-3 mb-0">
              <li v-for="(linha, index) in erroTransmissao.linhas" :key="index" class="mb-1">{{ linha }}</li>
            </ul>
          </div>
          <div v-else class="text-muted font-weight-bold">
            {{ erroTransmissao.mensagem }}
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Modal de Detalhes do Evento -->
    <b-modal
      ref="modalDetalhesEvento"
      title="Detalhes do Evento"
      size="xl"
      ok-only
      ok-title="Fechar"
      header-class="modal-header-premium bg-gradient-info text-white"
      body-class="p-0"
      footer-class="bg-light"
      centered
    >
      <div class="p-4 bg-dark">
        <b-form-textarea
          v-model="eventoDetalhes.formatted"
          rows="20"
          readonly
          class="border-0 text-success bg-dark font-weight-bold"
          style="font-family: 'Courier New', Courier, monospace; font-size: 13px; resize: none;"
        />
      </div>
    </b-modal>

    <!-- Modal de Carta de Correção -->
    <danfe-email-modal ref="modal-danfe" @sent="onDanfeSent" />

    <!-- Modal de Carta de Correção -->
    <carta-correcao-modal ref="modal-carta-correcao" @carta-enviada="onCartaEnviada" />
    
    <!-- Modal de Cancelamento -->
    <cancelar-nfe-modal ref="modal-cancelar-nfe" @nfe-cancelada="onNfeCancelada" />

    <!-- Modal de Detalhamento de Impostos -->
    <nfe-impostos-modal ref="modal-impostos" :nfe="nfe" />
  </div>
</template>


<script>

import VueAutonumeric from 'vue-autonumeric'

import NfeAbaDados from './partials/NfeAbaDados.vue'
import NfeAbaNfeReferenciada from './partials/NfeAbaNfeReferenciada.vue'
import NfeAbaEmitente from './partials/NfeAbaEmitente.vue'
import NfeAbaDestinatario from './partials/NfeAbaDestinatario.vue'
import NfeAbaProdutos from './partials/NfeAbaProdutos.vue'
import NfeAbaTransporte from './partials/NfeAbaTransporte.vue'
import NfeAbaCobranca from './partials/NfeAbaCobranca.vue'
import NfeAbaInfoAdic from './partials/NfeAbaInfoAdic.vue'
import NfePainelTotais from './partials/NfePainelTotais.vue'
import DanfeEmailModal from './partials/DanfeEmailModal.vue'
import CartaCorrecaoModal from './partials/CartaCorrecaoModal.vue'
import CancelarNfeModal from './partials/CancelarNfeModal.vue'
import NfeImpostosModal from './partials/NfeImpostosModal.vue'
import NfeProdutoIpi from './partials/NfeProdutoIpi.vue'
import NfeProdutoIcms from './partials/NfeProdutoIcms.vue'
import NfeProdutoPis from './partials/NfeProdutoPis.vue'
import NfeProdutoCofins from './partials/NfeProdutoCofins.vue'
import NfeProdutoImportacao from './partials/NfeProdutoImportacao.vue'
import HeaderPadrao from '@/components/Cec/HeaderPadrao.vue'
import Tools from '@/tools/tools.js'


export default {
  name: 'NfeCadastro',
  components: {
    VueAutonumeric,
    NfeAbaDados,
    NfeAbaNfeReferenciada,
    NfeAbaEmitente,
    NfeAbaDestinatario,
    NfeAbaProdutos,
    NfeAbaTransporte,
    NfeAbaCobranca,
    NfeAbaInfoAdic,
    NfePainelTotais,
    DanfeEmailModal,
    CartaCorrecaoModal,
    CancelarNfeModal,
    NfeImpostosModal,
    NfeProdutoIpi,
    NfeProdutoIcms,
    NfeProdutoPis,
    NfeProdutoCofins,
    NfeProdutoImportacao,
    CFOPDropDown: () => import('@/views/componentes/dropdown/CFOPDropDown.vue'),
    ProdutoPesquisa: () => import('@/views/pesquisa/ProdutoPesquisa.vue'),
    HeaderPadrao
  },
  props: {
    id: { type: [String, Number], default: null }
  },
  data() {
    return {
      nfe: {
        ID: null,
        ID_EMPRESA: null,
        ID_CLIENTE_NFE: null,
        TX_NUM_NFE: '',
        TX_CHAVE_ACESSO: '',
        TX_MODELO: '55',
        ID_SERIE: '',
        DT_EMISSAO: '',
        DT_SADIA_ENTRADA: '',
        
        // Status e controle
        OP_STATUS: '5', // Status "Em digitação" - permite edição
        TX_NUM_PROTOCOLO: '',
        TX_NUM_RECIBO: '',
        TX_SITUACAO: '',
        
        // Natureza da operação
        OP_NATUREZA_OPERACAO: '',
        TX_NATUREZA_OPERACAO: '',
        OP_TIPO_DOCUMENTO: '1',
        OP_TIPO_IMPRESSAO: '1',
        OP_FORMA_EMISSAO: '1',
        OP_FINALIDADE_EMISSAO: '1',
        OP_DESTINO_OPERACAO: '0',
        OP_CONSUMIDOR_FINAL: '1',
        OP_TIPO_ATENDIMENTO: '0',
        
        // Emitente
        TX_NUM_CNPJ_EMIT: '',
        TX_RAZAO_SOCIAL_EMIT: '',
        TX_NOME_FANTASIA_EMIT: '',
        TX_NUM_IE_EMIT: '',
        TX_NUM_IE_SUBST_EMIT: '',
        TX_NUM_IM_EMIT: '',
        TX_CNAE_EMIT: '',
        TX_LOGRADOURO_EMIT: '',
        TX_NUMERO_EMIT: '',
        TX_COMPLEMENTO_EMIT: '',
        TX_BAIRRO_EMIT: '',
        TX_PAIS_EMIT: 'BRASIL',
        ID_UF_EMIT: '',
        ID_MUNICIPIO_EMIT: '',
        TX_CEP_EMIT: '',
        TX_FONE_EMIT: '',
        
        // Destinatário
        OP_TIPO_DOC_DEST: '',
        TX_NUM_CNPJ_DEST: '',
        TX_RAZAO_SOCIAL_DEST: '',
        OP_ICMS_ISENTO_DEST: '0',
        TX_NUM_IE_DEST: '',
        TX_NUM_SUFRAMA_DEST: '',
        TX_LOGRADOURO_DEST: '',
        TX_NUMERO_DEST: '',
        TX_COMPLEMENTO_DEST: '',
        TX_BAIRRO_DEST: '',
        TX_CEP_DEST: '',
        ID_UF_DEST: '',
        ID_MUNICIPIO_DEST: '',
        TX_FONE_DEST: '',
        TX_EMAIL_DEST: '',
        
        // Totais
        VL_BASE_CALC_ICMS: 0,
        VL_TOTAL_ICMS: 0,
        VL_BASE_CALC_ST_ICMS: 0,
        VL_TOTAL_ST_ICMS: 0,
        VL_TOTAL_PRODUTOS: 0,
        VL_TOTAL_FRETE: 0,
        VL_TOTAL_SEGURO: 0,
        VL_TOTAL_DESCONTO: 0,
        VL_TOTAL_II: 0,
        VL_TOTAL_IPI: 0,
        VL_TOTAL_PIS: 0,
        VL_TOTAL_COFINS: 0,
        VL_TOTAL_OUTRAS_DESP: 0,
        VL_TOTAL_NFE: 0,
        
        // Transporte
        OP_MODALIDADE_FRETE: '1',
        ID_TRANSPORTADOR: null,
        TX_RAZAO_SOCIAL_TRANSP: '',
        TX_NUM_CNPJ_TRANSP: '',
        TX_NUM_IE_TRANSP: '',
        TX_PLACA_VEIC_FRETE: '',
        ID_UF_PLACA_VEIC_FRETE: '',
        NR_PESO_BRUTO: 0,
        NR_PESO_LIQUIDO: 0,
        
        // Cobrança
        TX_FATURA_NUMERO: '',
        VL_FATURA_VL_ORIGINAL: 0,
        VL_FATURA_VL_DESCONTO: 0,
        VL_FATURA_VL_LIQUIDO: 0,
        OP_FORMA_PAGAMENTO: null,
        NR_PARCELAS: null,
        
        // Informações adicionais
        OB_ADICIONAIS_FISCO: '',
        OB_COMPLEMENT_CONTRIBUINTE: '',
        OB_COMPLEMENT_CONTRIBUINTE_ORIG: '',
        OBS_OUTROS: '',
        
        // Outros
        CFOP_ID: '',
        TIPO_MOVIMENTACAO_ID: null,
        ID_SEQUENCIA_VENDAS: null,
        TX_HORA_ENTRA_SAI: '',
        TX_NUM_DIGEST_VALUE: '',
        DT_PROTOCOLO_NFE: '',
        
        // Reforma Tributária
        VL_IBSCBS_BASE_TOTAL: 0,
        VL_IBSUF_TOTAL: 0,
        VL_IBSMUN_TOTAL: 0,
        VL_CBS_TOTAL: 0,
        OP_REFORMA_TRIBUTARIA: '0',
        CMUNFGIBS: null,
        TPNF_DEBITO: null,
        TPNF_CREDITO: null,
        TP_ENTE_GOV: null,
        PREDUTOR_GOV: null,
        TP_OPER_GOV: null,
        VISTOT: 0,
        VBCIBSCBSTOT: 0,
        VIBSTOT: 0,
        VCREDPRES_TOT: 0,
        VCREDPRESCONDSUS_TOT: 0,
        VDEVTRIB_IBSUF_TOT: 0,
        VDIF_IBSUF_TOT: 0,
        VDEVTRIB_IBSMUN_TOT: 0,
        VDIF_IBSMUN_TOT: 0,
        VCBS_TOT: 0,
        VCREDPRES_CBS_TOT: 0,
        VCREDPRESCONDSUS_CBS_TOT: 0,
        VL_CREDITO_PRESUMIDO: 0,
        
        // Arrays
        itens: [],
        parcelas: []
      },
      // Flag para indicar que está carregando dados do backend
      // Quando true, os componentes filhos NÃO devem executar ações automáticas
      isLoadingFromBackend: false,
      totaisKey: 0,
      abasKey: 0,
      activeTab: 0,
      loadingTabs: {
        dados: false,
        referenciadas: false,
        emitente: false,
        dest: false,
        produtos: false,
        transporte: false,
        cobranca: false,
        info: false,
        eventos: false
      },
      erroTransmissao: {
        mensagem: '',
        linhas: []
      },
      eventos: [],
      loadingEventos: false,
      eventosFields: [
        { key: 'OP_TIPO', label: 'Tipo', class: 'text-center' },
        { key: 'DATA', label: 'Data', class: 'text-center' },
        { key: 'TX_NUM_PROTOCOLO', label: 'Protocolo' },
        { key: 'TX_NUM_RECIBO', label: 'Recibo' },
        { key: 'SEQUENCIA', label: 'Sequência', class: 'text-center' },
        { key: 'USUARIO', label: 'Usuário' },
        { key: 'acoes', label: 'Ações', class: 'text-center' }
      ],
      eventoDetalhes: {
        xml: '',
        formatted: ''
      },
      consultandoStatusServico: false,
      itensNfe: [],
      itemModalBusy: false,
      itemPreviewTimer: null,
      itemPreviewAplicando: false,
      itemPreviewHabilitado: false,
      itemActiveTab: 0,
      itemEditingIndex: null,
      itemEditingItem: null,
      itemProdutoNomeCache: '',
      itemUnidadeOptions: [],
      itemCfopOptions: [],
      itemCstIbscbsOptions: [
        { value: '000', text: '000 - Tributada integralmente' },
        { value: '010', text: '010 - Tributada com cobrança de IBS e CBS por substituição tributária' },
        { value: '011', text: '011 - Tributada com cobrança de IBS e CBS por antecipação' },
        { value: '200', text: '200 - Isenta de IBS e CBS' },
        { value: '220', text: '220 - Imune de IBS e CBS' },
        { value: '221', text: '221 - Imune de CBS com incidência de IBS' },
        { value: '222', text: '222 - Imune de IBS com incidência de CBS' },
        { value: '400', text: '400 - Não tributada pelo IBS e CBS' },
        { value: '410', text: '410 - Não incidência de IBS e CBS' },
        { value: '510', text: '510 - Suspensão do IBS e CBS' },
        { value: '515', text: '515 - Diferimento de IBS e CBS' },
        { value: '550', text: '550 - IBS e CBS com desoneração' },
        { value: '620', text: '620 - Tributada pelo regime favorecido' },
        { value: '800', text: '800 - Exportação com direito a crédito' },
        { value: '810', text: '810 - Vendas de bens e serviços de varejo para uso e consumo com direito a crédito' },
        { value: '811', text: '811 - Operação com direito a crédito presumido' },
        { value: '820', text: '820 - Operação de importação para revenda com direito a crédito' },
        { value: '830', text: '830 - Crédito de bens de capital' }
      ],
      itemAutonumericOptions: {
        pecas: {
          digitGroupSeparator: '.',
          decimalCharacter: ',',
          decimalPlaces: 0,
          minimumValue: '0',
          emptyInputBehavior: 'zero'
        },
        quantidade: {
          digitGroupSeparator: '.',
          decimalCharacter: ',',
          decimalPlaces: 5,
          minimumValue: '0',
          emptyInputBehavior: 'zero'
        },
        valor: {
          currencySymbol: 'R$ ',
          currencySymbolPlacement: 'p',
          digitGroupSeparator: '.',
          decimalCharacter: ',',
          decimalPlaces: 5,
          minimumValue: '0',
          emptyInputBehavior: 'zero'
        }
      },
      itemModalLocal: {
        reforma: {},
        importacao: {}
      }
    }
  },
  async created() {
    // Garantir que o wait seja fechado ao entrar no cadastro
    this.$store.commit('controle/setAguardando', false)
    this.itemModalLocal = this.getEmptyItemLocal()
    
    const routeId = this.id || this.$route && this.$route.params && this.$route.params.id
    // created hook: route id is resolved below
    if (routeId) {
      await this.carregarNfe(routeId)
    }
    this.$store.commit('controle/setAguardando', false)
  },
  mounted() {
    // keyboard shortcuts: CTRL+S save, ESC close modals
    // TODO: Map other Delphi shortcuts (origem: Unt_NotaFiscalEletronica.pas)
    window.addEventListener('keydown', this.onKeydown)
    // heartbeat removed (diagnostic logs); keep basic mount behavior
    
    // Garantir que o loading global está fechado após montagem (proteção básica)
    this.$nextTick(() => {
      this.$store.commit('controle/setAguardando', false)
    })
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeydown)
    if (this.itemPreviewTimer) {
      clearTimeout(this.itemPreviewTimer)
      this.itemPreviewTimer = null
    }
    if (this._heartbeatInterval) {
      clearInterval(this._heartbeatInterval)
      this._heartbeatInterval = null
    }
  },
  watch: {
    itemModalLocal: {
      deep: true,
      handler() {
        if (!this.itemPreviewHabilitado) return
        this.agendarPreviewItem()
      }
    }
  },
  computed: {
    subtitulo() {
      const parts = []
      
      if (this.nfe.ID) {
        parts.push(`#${this.nfe.ID}`)
      }
      
      if (this.nfe.TX_RAZAO_SOCIAL_DEST) {
        parts.push(this.nfe.TX_RAZAO_SOCIAL_DEST)
      }
      
      return parts.length > 0 ? parts.join(' - ') : ''
    },
    
    podeEditar() {
      // Somente status 5 (Em digitação) permite edição
      return this.nfe.OP_STATUS === '5' || this.nfe.OP_STATUS === 5
    },
    
    nfeAssinada() {
      // Status 1 (Assinada) permite transmitir ou clicar em Editar para voltar a digitar
      return this.nfe.OP_STATUS === '1' || this.nfe.OP_STATUS === 1
    },
    nfeRejeitada() {
      // Status 7 (Rejeitada) indica que a nota foi rejeitada pela SEFAZ
      return this.nfe.OP_STATUS === '7' || this.nfe.OP_STATUS === 7
    },
    
    nfeAutorizada() {
      // Somente status 2 (Autorizada) permite imprimir, enviar email e exportar XML
      return this.nfe.OP_STATUS === '2' || this.nfe.OP_STATUS === 2
    },
    
    statusDescricao() {
      const statusMap = {
        '0': 'Excluída',
        '1': 'Assinada',
        '2': 'Autorizada',
        '3': 'Cancelada',
        '4': 'Denegada',
        '5': 'Em Digitação',
        '6': 'Em Proc. Sefaz',
        '7': 'Rejeitada',
        '8': 'Transmitida Pendência',
        '9': 'Validada'
      }
      
      const status = String(this.nfe.OP_STATUS ?? '')
      return statusMap[status] || 'Desconhecido'
    },
    
    statusIcon() {
      const iconMap = {
        '0': 'fa fa-flag',
        '1': 'fa fa-pencil-square-o',
        '2': 'fa fa-check-circle',
        '3': 'fa fa-ban',
        '4': 'fa fa-exclamation-triangle',
        '5': 'fa fa-edit',
        '6': 'fa fa-spinner fa-spin',
        '7': 'fa fa-times-circle',
        '8': 'fa fa-clock-o',
        '9': 'fa fa-check'
      }
      return iconMap[String(this.nfe.OP_STATUS ?? '')] || 'fa fa-question-circle'
    },

    statusBadgeStyle() {
      const colorMap = {
        '0': { color: '#ffffff', background: '#FF0000' }, // EXCLUIDA
        '1': { color: '#ffffff', background: '#A9D08E' }, // ASSINADA
        '2': { color: '#ffffff', background: '#54B435' }, // AUTORIZADA
        '3': { color: '#ffffff', background: '#ED1C24' }, // CANCELADA
        '4': { color: '#333333', background: '#BFBFBF' }, // DENEGADA
        '5': { color: '#333333', background: '#FFC000' }, // EM DIGITAÇÃO
        '6': { color: '#ffffff', background: '#FF8200' }, // EM PROC. SEFAZ
        '7': { color: '#ffffff', background: '#A54D1B' }, // REJEITADA
        '8': { color: '#ffffff', background: '#204E71' }, // TRANSMITIDA PENDENCIA
        '9': { color: '#ffffff', background: '#00B0F0' }  // VALIDADA
      }
      return colorMap[String(this.nfe.OP_STATUS ?? '')] || { color: '#ffffff', background: '#6c757d' }
    },
    
    statusTextoCompleto() {
      const status = String(this.nfe.OP_STATUS || '')
      const descricao = this.statusDescricao
      return descricao ? `${status} - ${descricao}` : ''
    },
    
    statusBadgeClass() {
      const status = String(this.nfe.OP_STATUS || '')
      const classMap = {
        '2': 'badge badge-success',      // Autorizada - success (verde)
        '5': 'badge badge-info',         // Em Digitação - info (azul)
        '6': 'badge badge-warning text-dark' // Em Processamento na Sefaz - warning (marrom)
      }
      
      return classMap[status] || 'badge badge-secondary'
    },

    statusBadgeClassPremium() {
      const status = String(this.nfe.OP_STATUS || '')
      const classMap = {
        '2': 'success',      // Autorizada
        '5': 'info',         // Em Digitação
        '6': 'warning',      // Em Processamento
        '3': 'danger',       // Cancelada
        '7': 'danger'        // Rejeitada
      }
      return classMap[status] || 'secondary'
    },

    itemModalEdicao() {
      return this.itemEditingItem !== null
    },

    itemValorTotalBruto() {
      const item = this.itemModalLocal || {}
      const qtd = Number(item.quantidadeComercial) || 0
      const vlUnit = Number(item.valorUnitarioComercial) || 0
      const desconto = Number(item.valorTotalDesconto) || 0
      return (qtd * vlUnit) - desconto
    }
  },
  methods: {
    formatCurrency(value) {
      return Tools.formatCurrency(value)
    },

    getEmptyItemImportacao() {
      return {
        numeroDI: '',
        dataDI: null,
        viaTransporte: null,
        localDesembaraco: '',
        ufDesembaraco: '',
        dataDesembaraco: null,
        codigoExportador: '',
        valorAFRMM: 0
      }
    },

    getEmptyItemReforma() {
      return {
        cst_ibscbs: null,
        classtrib_ibscbs: '',
        vbc_ibscbs: 0,
        vibsurf: 0,
        vcbs: 0,
        vbcis: 0,
        vis: 0,
        pibsuf: 0,
        predaliq_ibsuf: 0,
        pcbs: 0,
        predaliq_cbs: 0,
        pis_seletivo: 0,
        pisespec: 0,
        pdif_ibsuf: 0,
        vdif_ibsuf: 0,
        vdevtrib_ibsuf: 0,
        paliqefet_ibsuf: 0,
        pibsmun: 0,
        vibsmun: 0,
        pdif_ibsmun: 0,
        vdif_ibsmun: 0,
        vdevtrib_ibsmun: 0,
        predaliq_ibsmun: 0,
        paliqefet_ibsmun: 0,
        pdif_cbs: 0,
        vdif_cbs: 0,
        vdevtrib_cbs: 0,
        paliqefet_cbs: 0,
        opReformaTributaria: null,
        indBemMovelUsado: 'N',
        ind_bem_movel_usado: 'N',
        vitemReforma: 0,
        vitem_reforma: 0,
        chaveDfeRef: '',
        nitemDfeRef: null,
        cstis: null,
        cclasstribis: '',
        utribis: '',
        qtribis: 0
      }
    },

    getEmptyItemLocal() {
      return {
        ID: null,
        idProduto: null,
        cfopId: this.nfe ? this.nfe.CFOP_ID : null,
        ean: '',
        ncm: '',
        cest: '',
        idUnidadeComercial: '',
        quantidadeComercial: 0,
        pecas: 0,
        valorUnitarioComercial: 0,
        valorTotalDesconto: 0,
        compoeValorNfe: true,
        idCentroEstoque: null,
        OP_ICMS_SITUACAO_TRIBUTARIA: null,
        OP_ICMS_ORIGEM: null,
        CBENEF: null,
        OP_ICMS_MODAL_DETERM_BC: null,
        VL_ICMS_PERCENT_REDUC_BC: 0,
        VL_ICMS_BASE_CALC: 0,
        VL_ICMS_ALIQUOTA: 0,
        VL_ICMS: 0,
        OP_ICMS_MODAL_DETERM_BC_ST: null,
        VL_ICMS_MARGEM_VL_ADIC_ST: 0,
        VL_ICMS_PERCENT_REDUC_BC_ST: 0,
        VL_ICMS_BASE_CALC_ST: 0,
        VL_ICMS_ALIQUOTA_ST: 0,
        VL_ICMS_ST: 0,
        VL_ICMS_BC_ST_RETIDO_ANT: 0,
        VL_ICMS_ST_RETIDO_ANT: 0,
        VL_ICMS_PERC_BC_OPER_PROPRIA: 0,
        ID_UF_ICMS_ST: '',
        OP_MOTIVO_DESONERACAO: null,
        VL_ALIQ_APLICAVEL_CALC_CREDITO: 0,
        VL_CREDITO_ICMS_APROVEITADO: 0,
        VL_ICMS_ALIQUOTA_DESONERA: 0,
        VL_ICMS_DESONERA: 0,
        OP_IND_DEDUZ_DESONERA: null,
        OP_IPI_SITUACAO_TRIBUTARIA: null,
        OP_IPI_CLASS_ENQ_CIG_BEB: null,
        TX_IPI_CODIGO_ENQUAD_LEGAL: '',
        TX_IPI_CNPJ_PRODUTOR: '',
        TX_IPI_NUM_SELO_CONTROLE: '',
        OP_IPI_TIPO_CALCULO: null,
        VL_IPI_BASE_CALC: 0,
        VL_IPI_ALIQUOTA: 0,
        VL_IPI_QUANT_TOTAL_UND_PADRAO: 0,
        VL_IPI_UNIDADE: 0,
        VL_IPI: 0,
        OP_PIS_SITUACAO_TRIBUTARIA: null,
        OP_PIS_TIPO_CALCULO: null,
        VL_PIS_BASE_CALC: 0,
        VL_PIS_ALIQUOTA_PERCENT: 0,
        VL_PIS_ALIQUOTA_REAIS: 0,
        VL_PIS_QUANT_VENDIDA: 0,
        VL_PIS: 0,
        OP_PISST_TIPO_CALCULO: null,
        VL_PISST_BASE_CALC: 0,
        VL_PISST_ALIQUOTA_PERCENT: 0,
        VL_PISST_ALIQUOTA_REAIS: 0,
        VL_PISST_QUANT_VENDIDA: 0,
        VL_PISST: 0,
        OP_COFINS_SITUACAO_TRIBUTARIA: null,
        OP_COFINS_TIPO_CALCULO: null,
        VL_COFINS_BASE_CALC: 0,
        VL_COFINS_ALIQUOTA_PERCENT: 0,
        VL_COFINS_ALIQUOTA_REAIS: 0,
        VL_COFINS_QUANT_VENDIDA: 0,
        VL_COFINS: 0,
        OP_COFINSST_TIPO_CALCULO: null,
        VL_COFINSST_BASE_CALC: 0,
        VL_COFINSST_ALIQUOTA_PERCENT: 0,
        VL_COFINSST_ALIQUOTA_REAIS: 0,
        VL_COFINSST_QUANT_VENDIDA: 0,
        VL_COFINSST: 0,
        importacao: this.getEmptyItemImportacao(),
        reforma: this.getEmptyItemReforma()
      }
    },

    getItemInitialLocal() {
      if (!this.itemEditingItem) return this.getEmptyItemLocal()

      const item = this.itemEditingItem
      const vazio = this.getEmptyItemLocal()
      return {
        ...vazio,
        ...item,
        ID: item.ID || item.id || vazio.ID,
        idProduto: item.idProduto || item.ID_PRODUTO || item.codigo || vazio.idProduto,
        cfopId: item.cfopId || item.ID_CFOP || vazio.cfopId,
        ean: item.ean || item.TX_NUM_EAN || vazio.ean,
        ncm: item.ncm || item.TX_NCM || vazio.ncm,
        cest: item.cest || item.CEST || vazio.cest,
        idUnidadeComercial: item.idUnidadeComercial || item.unidade || item.ID_UNIDADE_COMERCIAL || vazio.idUnidadeComercial,
        quantidadeComercial: item.quantidadeComercial || item.qtd || item.NR_QUANT_COMERCIAL || vazio.quantidadeComercial,
        pecas: item.pecas || item.PECAS || vazio.pecas,
        valorUnitarioComercial: item.valorUnitarioComercial || item.vl_unitario || item.VL_UNITARIO_COMERCIAL || vazio.valorUnitarioComercial,
        valorTotalDesconto: item.valorTotalDesconto || item.vl_desconto || item.VL_TOTAL_DESCONTO || vazio.valorTotalDesconto,
        compoeValorNfe: item.compoeValorNfe !== undefined ? item.compoeValorNfe : (item.OP_COMPOE_VALOR_NFE === '0' ? false : true),
        idCentroEstoque: item.idCentroEstoque || item.ID_CENTRO_ESTOQUE || vazio.idCentroEstoque,
        importacao: { ...this.getEmptyItemImportacao(), ...(item.importacao || {}) },
        reforma: { ...this.getEmptyItemReforma(), ...((item.impostos && item.impostos.reforma) || item.reforma || {
          cst_ibscbs: item.CST_IBSCBS || null,
          classtrib_ibscbs: item.CCLASSTRIB_IBSCBS || '',
          vbc_ibscbs: item.VBC_IBSCBS || 0,
          vibsurf: item.VIBSUF || 0,
          vcbs: item.VCBS || 0,
          vbcis: item.VBCIS || 0,
          vis: item.VIS || 0,
          pibsuf: item.PIBSUF || item.pibsuf || 0,
          pcbs: item.PCBS || item.pcbs || 0,
          pis_seletivo: item.PIS_SELETIVO || item.pis_seletivo || 0,
          pisespec: item.PISESPEC || item.pisespec || 0,
          pdif_ibsuf: item.PDIF_IBSUF || item.pdif_ibsuf || 0,
          vdif_ibsuf: item.VDIF_IBSUF || item.vdif_ibsuf || 0,
          vdevtrib_ibsuf: item.VDEVTRIB_IBSUF || item.vdevtrib_ibsuf || 0,
          predaliq_ibsuf: item.PREDALIQ_IBSUF || item.predaliq_ibsuf || 0,
          paliqefet_ibsuf: item.PALIQEFET_IBSUF || item.paliqefet_ibsuf || 0,
          pibsmun: item.PIBSMUN || item.pibsmun || 0,
          vibsmun: item.VIBSMUN || item.vibsmun || 0,
          pdif_ibsmun: item.PDIF_IBSMUN || item.pdif_ibsmun || 0,
          vdif_ibsmun: item.VDIF_IBSMUN || item.vdif_ibsmun || 0,
          vdevtrib_ibsmun: item.VDEVTRIB_IBSMUN || item.vdevtrib_ibsmun || 0,
          predaliq_ibsmun: item.PREDALIQ_IBSMUN || item.predaliq_ibsmun || 0,
          paliqefet_ibsmun: item.PALIQEFET_IBSMUN || item.paliqefet_ibsmun || 0,
          pdif_cbs: item.PDIF_CBS || item.pdif_cbs || 0,
          vdif_cbs: item.VDIF_CBS || item.vdif_cbs || 0,
          vdevtrib_cbs: item.VDEVTRIB_CBS || item.vdevtrib_cbs || 0,
          predaliq_cbs: item.PREDALIQ_CBS || item.predaliq_cbs || 0,
          paliqefet_cbs: item.PALIQEFET_CBS || item.paliqefet_cbs || 0,
          opReformaTributaria: item.OP_REFORMA_TRIBUTARIA || item.opReformaTributaria || null,
          indBemMovelUsado: item.IND_BEM_MOVEL_USADO || item.indBemMovelUsado || null,
          vitemReforma: item.VITEM_REFORMA || item.vitemReforma || 0,
          chaveDfeRef: item.CHAVE_DFE_REF || item.chaveDfeRef || '',
          nitemDfeRef: item.NITEM_DFE_REF || item.nitemDfeRef || null,
          cstis: item.CSTIS || item.cstis || null,
          cclasstribis: item.CCLASSTRIBIS || item.cclasstribis || '',
          utribis: item.UTRIBIS || item.utribis || '',
          qtribis: item.QTRIBIS || item.qtribis || 0
        }) }
      }
    },

    async carregarItensNfe() {
      if (!this.nfe.ID) {
        this.itensNfe = []
        return
      }

      try {
        this.loadingTabs.produtos = true
        const data = await this.$store.dispatch('nfe/GET_ITENS_BY_NFE_ID', this.nfe.ID)
        this.itensNfe = data || []
      } catch (err) {
        console.error('Erro ao carregar itens da NF-e:', err)
        this.toastItemNfe('Erro ao carregar produtos da NF-e', 'danger')
      } finally {
        this.loadingTabs.produtos = false
      }
    },

    async abrirModalItemNovo() {
      if (this.itemModalBusy) return

      if (!this.nfe.ID_EMPRESA) {
        this.toastItemNfe('Informe a empresa antes de adicionar produtos', 'warning')
        return
      }
      if (!this.nfe.ID_CLIENTE_NFE) {
        this.toastItemNfe('Informe o cliente/destinatário antes de adicionar produtos', 'warning')
        return
      }
      if (!this.nfe.TIPO_MOVIMENTACAO_ID) {
        this.toastItemNfe('Informe o tipo de movimentação antes de adicionar produtos', 'warning')
        return
      }

      const salvou = await this.salvar({ silencioso: true })
      if (!salvou) {
        this.toastItemNfe('Erro ao salvar NF-e. Não é possível adicionar produtos.', 'danger')
        return
      }

      this.itemEditingIndex = null
      this.itemEditingItem = null
      this.itemModalLocal = this.getEmptyItemLocal()
      this.$nextTick(() => this.$refs.modalItemNfe.show())
    },

    async abrirModalItemEditar(idx) {
      if (this.itemModalBusy) return

      const item = this.itensNfe[idx]
      if (!item) return

      const salvou = await this.salvar({ silencioso: true })
      if (!salvou) {
        this.toastItemNfe('Erro ao sincronizar NF-e antes da edição do item.', 'danger')
        return
      }

      try {
        this.itemModalBusy = true
        if (item.ID) {
          const itemBackend = await this.$store.dispatch('nfe/GET_ITEM_BY_ID', item.ID)
          const itemData = Array.isArray(itemBackend) ? itemBackend[0] : itemBackend
          this.itemEditingItem = {
            ...itemData,
            idProduto: itemData.ID_PRODUTO,
            produtoNome: itemData.TX_DESCRICAO,
            cfopId: itemData.ID_CFOP,
            ean: itemData.TX_NUM_EAN || '',
            ncm: itemData.TX_NCM || '',
            cest: itemData.CEST || '',
            idUnidadeComercial: itemData.ID_UNIDADE_COMERCIAL || '',
            quantidadeComercial: itemData.NR_QUANT_COMERCIAL || 0,
            pecas: itemData.PECAS || 0,
            valorUnitarioComercial: itemData.VL_UNITARIO_COMERCIAL || 0,
            valorTotalDesconto: itemData.VL_TOTAL_DESCONTO || 0,
            valorTotalBruto: itemData.VL_TOTAL_BRUTO || 0,
            compoeValorNfe: itemData.OP_COMPOE_VALOR_NFE === '1',
            idCentroEstoque: itemData.ID_CENTRO_ESTOQUE || null
          }
        } else {
          this.itemEditingItem = JSON.parse(JSON.stringify(item))
        }
        this.itemEditingIndex = idx
        this.itemModalLocal = this.getItemInitialLocal()
      } catch (err) {
        console.error('Erro ao carregar item da NF-e:', err)
        this.toastItemNfe('Erro ao carregar dados do produto.', 'danger')
        return
      } finally {
        this.itemModalBusy = false
      }

      this.$nextTick(() => this.$refs.modalItemNfe.show())
    },

    async removerItemNfe(idx) {
      if (this.itemModalBusy) return

      const confirmado = await this.$bvModal.msgBoxConfirm('Deseja realmente excluir este item?', {
        title: 'Confirmar exclusão',
        okVariant: 'danger',
        okTitle: 'Sim',
        cancelTitle: 'Não'
      })
      if (!confirmado) return

      const item = this.itensNfe[idx]
      if (!item) return

      if (!item.ID) {
        this.itensNfe.splice(idx, 1)
        return
      }

      try {
        this.itemModalBusy = true
        await this.$store.dispatch('nfe/EXCLUIR_ITEM', {
          nfe: this.nfe,
          item: this.mapearItemParaBackend(item)
        })
        await this.carregarItensNfe()
        await this.onNfeAtualizada(null)
        this.toastItemNfe('Item excluído com sucesso', 'success')
      } catch (err) {
        console.error('Erro ao excluir item:', err)
        this.toastItemNfe('Erro ao excluir item', 'danger')
      } finally {
        this.itemModalBusy = false
      }
    },

    onItemModalShown() {
      this.itemPreviewHabilitado = false
      this.itemActiveTab = 0
      this.itemProdutoNomeCache = ''
      this.itemModalLocal = this.getItemInitialLocal()
      this.carregarUnidadesItemNfe()
      this.carregarCfopsItemNfe()

      if (this.itemModalEdicao) {
        this.itemProdutoNomeCache = this.itemEditingItem.TX_DESCRICAO || this.itemEditingItem.produtoNome || ''
        this.$nextTick(() => {
          if (this.$refs.produtoPesquisaNfe && this.$refs.produtoPesquisaNfe.atualizaComplete) {
            this.$refs.produtoPesquisaNfe.atualizaComplete()
          }
        })
      } else {
        this.$nextTick(() => {
          if (this.$refs.produtoPesquisaNfe && this.$refs.produtoPesquisaNfe.limpar) {
            this.$refs.produtoPesquisaNfe.limpar()
          }
        })
      }

      this.$nextTick(() => {
        if (!this.itemModalEdicao &&
            this.$refs.produtoPesquisaNfe &&
            this.$refs.produtoPesquisaNfe.$refs.auto &&
            this.$refs.produtoPesquisaNfe.$refs.auto.$refs.autocomplete) {
          this.$refs.produtoPesquisaNfe.$refs.auto.$refs.autocomplete.focus()
        }

        this.itemPreviewHabilitado = true
      })
    },

    onItemModalHidden() {
      if (this.itemPreviewTimer) clearTimeout(this.itemPreviewTimer)
      this.itemPreviewHabilitado = false
      this.itemActiveTab = 0
      this.itemEditingIndex = null
      this.itemEditingItem = null
      this.itemProdutoNomeCache = ''
      this.itemModalLocal = this.getEmptyItemLocal()
    },

    montarItemModalLocal() {
      const cfopText = this.itemCfopOptions.find(c => c.value === this.itemModalLocal.cfopId)?.text || ''

      return {
        ...this.itemModalLocal,
        ID_PRODUTO: this.itemModalLocal.idProduto,
        ID_CFOP: this.itemModalLocal.cfopId,
        TX_NUM_EAN: this.itemModalLocal.ean,
        TX_NCM: this.itemModalLocal.ncm,
        CEST: this.itemModalLocal.cest,
        ID_UNIDADE_COMERCIAL: this.itemModalLocal.idUnidadeComercial,
        ID_UNIDADE_TRIBUTARIA: this.itemModalLocal.idUnidadeComercial,
        NR_QUANT_COMERCIAL: this.itemModalLocal.quantidadeComercial,
        NR_QUANT_TRIBUTARIA: this.itemModalLocal.quantidadeComercial,
        VL_UNITARIO_COMERCIAL: this.itemModalLocal.valorUnitarioComercial,
        VL_UNIDADE_TRIBUTARIA: this.itemModalLocal.valorUnitarioComercial,
        VL_TOTAL_DESCONTO: this.itemModalLocal.valorTotalDesconto,
        VL_TOTAL_BRUTO: this.itemValorTotalBruto,
        OP_COMPOE_VALOR_NFE: this.itemModalLocal.compoeValorNfe ? '1' : '0',
        ID_CENTRO_ESTOQUE: this.itemModalLocal.idCentroEstoque,
        TX_DESCRICAO: this.itemProdutoNomeCache || this.itemModalLocal.TX_DESCRICAO || '',
        valorTotalBruto: this.itemValorTotalBruto,
        produtoNome: this.itemProdutoNomeCache,
        cfopDescricao: cfopText,
        reforma: {
          ...this.itemModalLocal.reforma
        }
      }
    },

    itemDeveCalcularPreview() {
      const item = this.itemModalLocal || {}
      return Boolean(
        item.idProduto &&
        item.cfopId &&
        Number(item.quantidadeComercial || 0) > 0 &&
        Number(item.valorUnitarioComercial || 0) > 0
      )
    },

    agendarPreviewItem() {
      if (this.itemPreviewAplicando || !this.itemDeveCalcularPreview()) return
      if (this.itemPreviewTimer) clearTimeout(this.itemPreviewTimer)
      this.itemPreviewTimer = setTimeout(() => {
        this.executarPreviewItem().catch(err => {
          console.error('Erro ao calcular prévia fiscal:', err)
        })
      }, 500)
    },

    async executarPreviewItem() {
      if (!this.itemDeveCalcularPreview()) return

      try {
        const itemParaBackend = this.mapearProdutoParaItemBackend(this.montarItemModalLocal())
        const resp = await this.$store.dispatch('nfe/CALCULAR_PREVIEW_ITEM', {
          nfe: this.nfe,
          item: itemParaBackend
        })
        const itemData = resp?.item || resp
        if (!itemData) return

        this.itemPreviewAplicando = true
        this.itemPreviewHabilitado = false
        this.mapearDadosItemFiscal(itemData)
        this.$nextTick(() => {
          this.itemPreviewAplicando = false
          this.itemPreviewHabilitado = true
        })
      } catch (err) {
        this.itemPreviewAplicando = false
        this.itemPreviewHabilitado = true
        throw err
      }
    },

    async salvarItemModal() {
      if (!this.validarItemModal()) return

      try {
        if (this.itemDeveCalcularPreview()) {
          await this.executarPreviewItem()
        }
      } catch (err) {
        this.toastItemNfe('Falha ao calcular tributação do produto', 'danger', err)
        return
      }

      try {
        this.itemModalBusy = true
        const itemParaBackend = this.mapearProdutoParaItemBackend(this.montarItemModalLocal())

        if (this.itemEditingIndex !== null && this.itemEditingItem && this.itemEditingItem.ID) {
          await this.$store.dispatch('nfe/EDITAR_ITEM', {
            nfe: this.nfe,
            item: itemParaBackend
          })
          this.toastItemNfe('Item atualizado com sucesso', 'success')
        } else {
          await this.$store.dispatch('nfe/SALVAR_ITEM', {
            nfe: this.nfe,
            item: itemParaBackend
          })
          this.toastItemNfe('Item adicionado com sucesso', 'success')
        }

        await this.carregarItensNfe()
        await this.onNfeAtualizada(null)
        this.$refs.modalItemNfe.hide()
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'danger', err)
      } finally {
        this.itemModalBusy = false
      }
    },

    validarItemModal() {
      const item = this.itemModalLocal || {}
      if (!item.idProduto) {
        this.toastItemNfe('Selecione um produto', 'warning')
        return false
      }
      if (!item.cfopId) {
        this.toastItemNfe('Selecione um CFOP', 'warning')
        return false
      }
      if (!item.quantidadeComercial || item.quantidadeComercial <= 0) {
        this.toastItemNfe('Informe a quantidade comercializada', 'warning')
        return false
      }
      if (!item.valorUnitarioComercial || item.valorUnitarioComercial <= 0) {
        this.toastItemNfe('Informe o valor unitário', 'warning')
        return false
      }
      return true
    },

    async onItemProdutoSelected(id) {
      if (!id || !this.itemModalLocal) return

      this.itemModalLocal.idProduto = id
      this.itemModalLocal.ID_PRODUTO = id

      try {
        this.itemModalBusy = true
        if (!this.itemModalEdicao && this.nfe) {
          await this.carregarDadosFiscaisItemProduto(id)
        } else {
          const resp = await this.$http.get(`/api/protected/produto/getById/${id}`)
          const produto = resp && resp.data ? (Array.isArray(resp.data) ? resp.data[0] : resp.data) : null
          if (produto) {
            this.aplicarDadosProdutoItem(produto)
          }
        }
      } catch (err) {
        console.error('Erro ao carregar produto da NF-e:', err)
        this.toastItemNfe('Falha ao carregar dados do produto', 'danger', err)
      } finally {
        this.itemModalBusy = false
      }
    },

    onItemProdutoNome(nome) {
      this.itemProdutoNomeCache = nome || ''
    },

    async carregarDadosFiscaisItemProduto(produtoId) {
      if (!this.nfe.ID || !this.nfe.ID_CLIENTE_NFE || !this.nfe.TIPO_MOVIMENTACAO_ID || !this.nfe.ID_EMPRESA) {
        this.toastItemNfe('Preencha empresa, destinatário e tipo de movimentação antes do item', 'warning')
        return
      }

      const url = `/api/protected/notaFiscal/item/getNovo/${this.nfe.ID}/${produtoId}/${this.nfe.ID_CLIENTE_NFE}/${this.nfe.TIPO_MOVIMENTACAO_ID}/${this.nfe.ID_EMPRESA}`
      const resp = await this.$http.get(url)
      if (resp && resp.data && resp.data.item) {
        this.mapearDadosItemFiscal(resp.data.item)
      }
    },

    mapearDadosItemFiscal(item) {
      if (!this.itemModalLocal) return

      const valorOuAtual = (valor, atual) => (valor !== undefined && valor !== null && valor !== '' ? valor : atual)
      const numeroPositivoOuAtual = (valor, atual) => {
        const numero = Number(valor)
        return Number.isFinite(numero) && numero > 0 ? valor : atual
      }

      Object.assign(this.itemModalLocal, item, {
        idProduto: item.ID_PRODUTO || item.COD_PRO || this.itemModalLocal.idProduto,
        cfopId: item.ID_CFOP || this.itemModalLocal.cfopId || (this.nfe ? this.nfe.CFOP_ID : null),
        ean: valorOuAtual(item.TX_NUM_EAN, this.itemModalLocal.ean) || '',
        ncm: valorOuAtual(item.TX_NCM, this.itemModalLocal.ncm) || '',
        cest: valorOuAtual(item.CEST, this.itemModalLocal.cest) || '',
        idUnidadeComercial: valorOuAtual(item.ID_UNIDADE_COMERCIAL, this.itemModalLocal.idUnidadeComercial) || '',
        idCentroEstoque: item.ID_CENTRO_ESTOQUE || item.COD_CENTRO || this.itemModalLocal.idCentroEstoque,
        quantidadeComercial: numeroPositivoOuAtual(item.NR_QUANT_COMERCIAL, this.itemModalLocal.quantidadeComercial || 0),
        valorUnitarioComercial: numeroPositivoOuAtual(item.VL_UNITARIO_COMERCIAL, this.itemModalLocal.valorUnitarioComercial || 0),
        valorTotalDesconto: valorOuAtual(item.VL_TOTAL_DESCONTO, this.itemModalLocal.valorTotalDesconto || 0),
        compoeValorNfe: item.OP_COMPOE_VALOR_NFE === '1' || item.OP_COMPOE_VALOR_NFE === true,
        pecas: valorOuAtual(item.PECAS, this.itemModalLocal.pecas || 0)
      })

      this.itemModalLocal.reforma = {
        ...this.getEmptyItemReforma(),
        cst_ibscbs: item.CST_IBSCBS,
        classtrib_ibscbs: item.CCLASSTRIB_IBSCBS || '',
        vbc_ibscbs: item.VBC_IBSCBS || 0,
        vibsurf: item.VIBSUF || 0,
        vcbs: item.VCBS || 0,
        vbcis: item.VBCIS || 0,
        vis: item.VIS || 0,
        pibsuf: item.PIBSUF || 0,
        pcbs: item.PCBS || 0,
        pis_seletivo: item.PIS_SELETIVO || 0,
        predaliq_ibsuf: item.PREDALIQ_IBSUF || 0,
        predaliq_cbs: item.PREDALIQ_CBS || 0,
        ind_bem_movel_usado: item.IND_BEM_MOVEL_USADO || 'N',
        vitem_reforma: item.VITEM_REFORMA || 0
      }

      this.$forceUpdate()
    },

    aplicarDadosProdutoItem(produto) {
      if (!this.itemModalLocal) return

      this.itemModalLocal.ean = produto.EAN || produto.CODBARRA || ''
      this.itemModalLocal.TX_NUM_EAN = this.itemModalLocal.ean
      this.itemModalLocal.ncm = produto.NCM || produto.TX_NCM || ''
      this.itemModalLocal.TX_NCM = this.itemModalLocal.ncm
      this.itemModalLocal.cest = produto.CEST || ''
      this.itemModalLocal.CEST = this.itemModalLocal.cest
      this.itemModalLocal.idUnidadeComercial = produto.UNIDADE || produto.ID_UNIDADE || ''
      this.itemModalLocal.ID_UNIDADE_COMERCIAL = this.itemModalLocal.idUnidadeComercial

      if (!this.itemModalLocal.cfopId && produto.ID_CFOP) {
        this.itemModalLocal.cfopId = produto.ID_CFOP
        this.itemModalLocal.ID_CFOP = produto.ID_CFOP
      }
    },

    async carregarUnidadesItemNfe() {
      try {
        const resp = await this.$http.get('/api/protected/unidadeComercial/getLista')
        const unidades = resp && resp.data ? resp.data : []
        this.itemUnidadeOptions = unidades.map(u => ({ value: u.ID, text: u.ID }))
      } catch (err) {
        console.error('Erro ao carregar unidades:', err)
        this.itemUnidadeOptions = []
      }
    },

    async carregarCfopsItemNfe() {
      try {
        const resp = await this.$http.get('/api/protected/cfop/listarTodos')
        const cfops = resp && resp.data ? resp.data : []
        this.itemCfopOptions = cfops.map(c => ({
          value: c.CONTADOR,
          text: `${c.CONTADOR} - ${c.UTILIZACAO || c.DESCRICAO}`
        }))
      } catch (err) {
        console.error('Erro ao carregar CFOPs:', err)
        this.itemCfopOptions = []
      }
    },

    calcularTotalItemModal() {
      this.$forceUpdate()
      this.agendarPreviewItem()
    },

    mapearProdutoParaItemBackend(produto) {
      const pick = (obj, ...keys) => {
        if (!obj) return null
        for (const key of keys) {
          const value = obj[key]
          if (value !== undefined && value !== null && value !== '') return value
        }
        return null
      }
      const parseNumero = (value) => {
        if (value === undefined || value === null || value === '') return 0
        if (typeof value === 'number') return Number.isFinite(value) ? value : 0

        const texto = String(value).trim()
        if (!texto) return 0

        const normalizado = texto.includes(',') ? texto.replace(/\./g, '').replace(',', '.') : texto
        const numero = Number(normalizado)
        return Number.isFinite(numero) ? numero : 0
      }
      const num = (obj, ...keys) => parseNumero(pick(obj, ...keys))
      const txt = (obj, ...keys) => pick(obj, ...keys) || ''

      // Monta o objeto normalmente
      const item = {
        ID: produto.ID || this.itemEditingItem?.ID || null,
        ID_NFE: this.nfe.ID,
        ID_PRODUTO: produto.idProduto,
        TX_DESCRICAO: produto.produtoNome || produto.TX_DESCRICAO || '',
        TX_NUM_EAN: produto.ean || '',
        TX_NCM: produto.ncm || '',
        CEST: produto.cest || '',
        ID_CFOP: produto.cfopId || this.nfe.CFOP_ID,
        TX_CFOP_GENERO: produto.cfopGenero || '',
        ID_UNIDADE_COMERCIAL: produto.idUnidadeComercial || '',
        NR_QUANT_COMERCIAL: produto.quantidadeComercial || 0,
        ID_UNIDADE_TRIBUTARIA: produto.idUnidadeTributaria || produto.idUnidadeComercial || '',
        VL_UNITARIO_COMERCIAL: produto.valorUnitarioComercial || 0,
        NR_QUANT_TRIBUTARIA: produto.quantidadeTributaria || produto.quantidadeComercial || 0,
        VL_UNIDADE_TRIBUTARIA: produto.valorUnidadeTributaria || produto.valorUnitarioComercial || 0,
        TX_NUM_EAN_TRIBUTARIA: produto.eanTributaria || '',
        VL_TOTAL_FRETE: produto.valorTotalFrete || 0,
        VL_TOTAL_SEGURO: produto.valorTotalSeguro || 0,
        VL_TOTAL_DESCONTO: produto.valorTotalDesconto || 0,
        VL_TOTAL_BRUTO: produto.valorTotalBruto || 0,
        OP_PRODUTO_ESPECIFICO: produto.produtoEspecifico || null,
        PECAS: produto.pecas || 0,
        OP_COMPOE_VALOR_NFE: produto.compoeValorNfe ? '1' : '0',
        ID_CENTRO_ESTOQUE: produto.idCentroEstoque || null,
        VL_DESCONTO_PERC: produto.descontoPercentual || 0,
        TX_OBSERVACOES: produto.observacoes || '',
        TX_EX_TIPI: produto.exTipi || '',
        OP_ICMS_SITUACAO_TRIBUTARIA: txt(produto, 'OP_ICMS_SITUACAO_TRIBUTARIA'),
        OP_ICMS_ORIGEM: txt(produto, 'OP_ICMS_ORIGEM'),
        CBENEF: txt(produto, 'CBENEF'),
        OP_ICMS_MODAL_DETERM_BC: txt(produto, 'OP_ICMS_MODAL_DETERM_BC'),
        VL_ICMS_PERCENT_REDUC_BC: num(produto, 'VL_ICMS_PERCENT_REDUC_BC'),
        VL_ICMS_BASE_CALC: num(produto, 'VL_ICMS_BASE_CALC'),
        VL_ICMS_ALIQUOTA: num(produto, 'VL_ICMS_ALIQUOTA'),
        VL_ICMS: num(produto, 'VL_ICMS'),
        OP_ICMS_MODAL_DETERM_BC_ST: txt(produto, 'OP_ICMS_MODAL_DETERM_BC_ST'),
        VL_ICMS_MARGEM_VL_ADIC_ST: num(produto, 'VL_ICMS_MARGEM_VL_ADIC_ST'),
        VL_ICMS_PERCENT_REDUC_BC_ST: num(produto, 'VL_ICMS_PERCENT_REDUC_BC_ST'),
        VL_ICMS_BASE_CALC_ST: num(produto, 'VL_ICMS_BASE_CALC_ST'),
        VL_ICMS_ALIQUOTA_ST: num(produto, 'VL_ICMS_ALIQUOTA_ST'),
        VL_ICMS_ST: num(produto, 'VL_ICMS_ST'),
        VL_ICMS_BC_ST_RETIDO_ANT: num(produto, 'VL_ICMS_BC_ST_RETIDO_ANT'),
        VL_ICMS_ST_RETIDO_ANT: num(produto, 'VL_ICMS_ST_RETIDO_ANT'),
        VL_ICMS_PERC_BC_OPER_PROPRIA: num(produto, 'VL_ICMS_PERC_BC_OPER_PROPRIA'),
        ID_UF_ICMS_ST: txt(produto, 'ID_UF_ICMS_ST'),
        VL_ALIQ_APLICAVEL_CALC_CREDITO: num(produto, 'VL_ALIQ_APLICAVEL_CALC_CREDITO'),
        VL_CREDITO_ICMS_APROVEITADO: num(produto, 'VL_CREDITO_ICMS_APROVEITADO'),
        VL_PICMSINTER: produto.VL_PICMSINTER || 0,
        VL_PICMSINTERPART: produto.VL_PICMSINTERPART || 0,
        VL_VFCPUFDEST: produto.VL_VFCPUFDEST || 0,
        VL_VICMSUFDEST: produto.VL_VICMSUFDEST || 0,
        VL_VICMSUFREMET: produto.VL_VICMSUFREMET || 0,
        VL_VBCUFDEST: produto.VL_VBCUFDEST || 0,
        VL_PFCPUFDEST: produto.VL_PFCPUFDEST || 0,
        VL_PICMSUFDEST: produto.VL_PICMSUFDEST || 0,
        VL_ICMS_PERC_DIF: produto.VL_ICMS_PERC_DIF || 0,
        VL_ICMS_DIFERIDO: produto.VL_ICMS_DIFERIDO || 0,
        VL_ICMS_OP_SEM_DIF: produto.VL_ICMS_OP_SEM_DIF || 0,
        VL_ICMS_ALIQUOTA_DESONERA: num(produto, 'VL_ICMS_ALIQUOTA_DESONERA'),
        VL_ICMS_DESONERA: num(produto, 'VL_ICMS_DESONERA'),
        OP_MOTIVO_DESONERACAO: txt(produto, 'OP_MOTIVO_DESONERACAO'),
        OP_IND_DEDUZ_DESONERA: txt(produto, 'OP_IND_DEDUZ_DESONERA'),
        // ...demais campos não ICMS...
      }

      // Lista de campos de ICMS para filtrar
      const camposICMS = [
        'OP_ICMS_SITUACAO_TRIBUTARIA', 'OP_ICMS_ORIGEM', 'CBENEF', 'OP_ICMS_MODAL_DETERM_BC',
        'VL_ICMS_PERCENT_REDUC_BC', 'VL_ICMS_BASE_CALC', 'VL_ICMS_ALIQUOTA', 'VL_ICMS',
        'OP_ICMS_MODAL_DETERM_BC_ST', 'VL_ICMS_MARGEM_VL_ADIC_ST', 'VL_ICMS_PERCENT_REDUC_BC_ST',
        'VL_ICMS_BASE_CALC_ST', 'VL_ICMS_ALIQUOTA_ST', 'VL_ICMS_ST', 'VL_ICMS_BC_ST_RETIDO_ANT',
        'VL_ICMS_ST_RETIDO_ANT', 'VL_ICMS_PERC_BC_OPER_PROPRIA', 'ID_UF_ICMS_ST',
        'VL_ALIQ_APLICAVEL_CALC_CREDITO', 'VL_CREDITO_ICMS_APROVEITADO',
        'VL_ICMS_ALIQUOTA_DESONERA', 'VL_ICMS_DESONERA', 'OP_MOTIVO_DESONERACAO', 'OP_IND_DEDUZ_DESONERA'
      ]

      // Remove campos de ICMS não preenchidos
      for (const campo of camposICMS) {
        const valor = item[campo]
        // Remove se for null, undefined, string vazia, ou zero (exceto campos obrigatórios)
        if (
          valor === null || valor === undefined ||
          (typeof valor === 'string' && valor.trim() === '') ||
          (typeof valor === 'number' && valor === 0)
        ) {
          delete item[campo]
        }
      }

      return item
    },

    mapearItemParaBackend(item) {
      return {
        ID: item.ID,
        ID_NFE: this.nfe.ID,
        ID_PRODUTO: item.ID_PRODUTO,
        TX_DESCRICAO: item.TX_DESCRICAO || '',
        TX_NUM_EAN: item.TX_NUM_EAN || '',
        TX_NCM: item.TX_NCM || '',
        CEST: item.CEST || '',
        ID_CFOP: item.ID_CFOP,
        ID_UNIDADE_COMERCIAL: item.ID_UNIDADE_COMERCIAL || '',
        NR_QUANT_COMERCIAL: item.NR_QUANT_COMERCIAL || 0,
        VL_UNITARIO_COMERCIAL: item.VL_UNITARIO_COMERCIAL || 0,
        VL_TOTAL_DESCONTO: item.VL_TOTAL_DESCONTO || 0,
        VL_TOTAL_BRUTO: item.VL_TOTAL_BRUTO || 0,
        PECAS: item.PECAS || 0,
        OP_COMPOE_VALOR_NFE: item.OP_COMPOE_VALOR_NFE || '1',
        OP_ICMS_SITUACAO_TRIBUTARIA: item.OP_ICMS_SITUACAO_TRIBUTARIA || '',
        VL_ICMS_BASE_CALC: item.VL_ICMS_BASE_CALC || 0,
        VL_ICMS_ALIQUOTA: item.VL_ICMS_ALIQUOTA || 0,
        VL_ICMS: item.VL_ICMS || 0,
        OP_IPI_SITUACAO_TRIBUTARIA: item.OP_IPI_SITUACAO_TRIBUTARIA || '',
        VL_IPI_BASE_CALC: item.VL_IPI_BASE_CALC || 0,
        VL_IPI_ALIQUOTA: item.VL_IPI_ALIQUOTA || 0,
        VL_IPI: item.VL_IPI || 0,
        OP_PIS_SITUACAO_TRIBUTARIA: item.OP_PIS_SITUACAO_TRIBUTARIA || '',
        VL_PIS_BASE_CALC: item.VL_PIS_BASE_CALC || 0,
        VL_PIS_ALIQUOTA_PERCENT: item.VL_PIS_ALIQUOTA_PERCENT || 0,
        VL_PIS: item.VL_PIS || 0,
        OP_COFINS_SITUACAO_TRIBUTARIA: item.OP_COFINS_SITUACAO_TRIBUTARIA || '',
        VL_COFINS_BASE_CALC: item.VL_COFINS_BASE_CALC || 0,
        VL_COFINS_ALIQUOTA_PERCENT: item.VL_COFINS_ALIQUOTA_PERCENT || 0,
        VL_COFINS: item.VL_COFINS || 0,
        CST_IBSCBS: item.CST_IBSCBS || null,
        CCLASSTRIB_IBSCBS: item.CCLASSTRIB_IBSCBS || '',
        VBC_IBSCBS: item.VBC_IBSCBS || 0,
        VIBSUF: item.VIBSUF || 0,
        VCBS: item.VCBS || 0,
        VBCIS: item.VBCIS || 0,
        VIS: item.VIS || 0
      }
    },

    toastItemNfe(msg, variant = 'info', err) {
      const detalhe = err && (err.message || err.response?.data || err.toString())
      this.$bvToast && this.$bvToast.toast(detalhe ? `${msg}: ${detalhe}` : msg, {
        title: 'Produto NFe',
        variant,
        autoHideDelay: 3500,
        solid: true
      })
    },

    getEventoTipoBadgePremium(tipo) {
      const map = {
        'T': 'badge-warning text-white', // Carta de Correção
        'C': 'badge-danger',             // Cancelamento
        'A': 'badge-success'             // Autorização
      }
      return map[tipo] || 'badge-secondary'
    },
    onKeydown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault()
        this.salvar()
      }
      if (e.key === 'Escape') {
        // close modals if any — TODO wire specific modals
      }
    },

    habilitarEdicao() {
      // Muda status para "Em Digitação" (5) para permitir modificações
      this.nfe.OP_STATUS = 5
      this.$bvToast.toast('Modo de edição habilitado. Lembre-se de salvar as alterações.', {
        title: 'Edição Habilitada',
        variant: 'info',
        solid: true
      })
    },

    async carregarNfe(id) {
      let manterLoadingAteMontagem = false
      this.loadingTabs.dados = true
      this.isLoadingFromBackend = true // Ativar flag ANTES de carregar
      try {
        const data = await this.$store.dispatch('nfe/GET_BY_ID', id)
          // backend may return array or object
          const registro = Array.isArray(data) ? data[0] : data
          if (!registro) {
            this.$bvToast.toast('NF-e não encontrada ou não retornada pelo backend.', { variant: 'danger', solid: true })
            this.isLoadingFromBackend = false
            this.loadingTabs.dados = false
            this.$store.commit('controle/setAguardando', false)
            return
          }

          // Usar Object.assign para atualizar sem causar loop infinito
          Object.assign(this.nfe, {
          ID: registro.ID || id,
          ID_EMPRESA: registro.ID_EMPRESA || null,
          ID_CLIENTE_NFE: registro.ID_CLIENTE_NFE || null,
          TX_NUM_NFE: registro.TX_NUM_NFE || '',
          TX_CHAVE_ACESSO: registro.TX_CHAVE_ACESSO || '',
          TX_MODELO: registro.TX_MODELO || '55',
          ID_SERIE: registro.ID_SERIE || '',
          DT_EMISSAO: registro.DT_EMISSAO ? Tools.formatarDataParaInput(registro.DT_EMISSAO) : '',
          DT_SADIA_ENTRADA: registro.DT_SADIA_ENTRADA ? Tools.formatarDataParaInput(registro.DT_SADIA_ENTRADA) : '',
          
          // Status e controle
          OP_STATUS: registro.OP_STATUS || '',
          TX_NUM_PROTOCOLO: registro.TX_NUM_PROTOCOLO || '',
          TX_NUM_RECIBO: registro.TX_NUM_RECIBO || '',
          TX_SITUACAO: registro.TX_SITUACAO || '',
          
          // Natureza da operação
          OP_NATUREZA_OPERACAO: registro.OP_NATUREZA_OPERACAO || '',
          TX_NATUREZA_OPERACAO: registro.TX_NATUREZA_OPERACAO || '',
          OP_TIPO_DOCUMENTO: registro.OP_TIPO_DOCUMENTO || '1',
          OP_TIPO_IMPRESSAO: registro.OP_TIPO_IMPRESSAO || '1',
          OP_FORMA_EMISSAO: registro.OP_FORMA_EMISSAO || '1',
          OP_FINALIDADE_EMISSAO: registro.OP_FINALIDADE_EMISSAO || '1',
          OP_DESTINO_OPERACAO: registro.OP_DESTINO_OPERACAO || '0',
          OP_CONSUMIDOR_FINAL: registro.OP_CONSUMIDOR_FINAL || '1',
          OP_TIPO_ATENDIMENTO: registro.OP_TIPO_ATENDIMENTO || '0',
          
          // Emitente
          TX_NUM_CNPJ_EMIT: registro.TX_NUM_CNPJ_EMIT || '',
          TX_RAZAO_SOCIAL_EMIT: registro.TX_RAZAO_SOCIAL_EMIT || '',
          TX_NOME_FANTASIA_EMIT: registro.TX_NOME_FANTASIA_EMIT || '',
          TX_NUM_IE_EMIT: registro.TX_NUM_IE_EMIT || '',
          TX_NUM_IE_SUBST_EMIT: registro.TX_NUM_IE_SUBST_EMIT || '',
          TX_NUM_IM_EMIT: registro.TX_NUM_IM_EMIT || '',
          TX_CNAE_EMIT: registro.TX_CNAE_EMIT || '',
          TX_LOGRADOURO_EMIT: registro.TX_LOGRADOURO_EMIT || '',
          TX_NUMERO_EMIT: registro.TX_NUMERO_EMIT || '',
          TX_COMPLEMENTO_EMIT: registro.TX_COMPLEMENTO_EMIT || '',
          TX_BAIRRO_EMIT: registro.TX_BAIRRO_EMIT || '',
          TX_PAIS_EMIT: registro.TX_PAIS_EMIT || 'BRASIL',
          ID_UF_EMIT: registro.ID_UF_EMIT || '',
          ID_MUNICIPIO_EMIT: registro.ID_MUNICIPIO_EMIT || '',
          TX_CEP_EMIT: registro.TX_CEP_EMIT || '',
          TX_FONE_EMIT: registro.TX_FONE_EMIT || '',
          
          // Destinatário
          OP_TIPO_DOC_DEST: registro.OP_TIPO_DOC_DEST || '',
          TX_NUM_CNPJ_DEST: registro.TX_NUM_CNPJ_DEST || '',
          TX_RAZAO_SOCIAL_DEST: registro.TX_RAZAO_SOCIAL_DEST || '',
          OP_ICMS_ISENTO_DEST: registro.OP_ICMS_ISENTO_DEST || '0',
          TX_NUM_IE_DEST: registro.TX_NUM_IE_DEST || '',
          TX_NUM_SUFRAMA_DEST: registro.TX_NUM_SUFRAMA_DEST || '',
          TX_LOGRADOURO_DEST: registro.TX_LOGRADOURO_DEST || '',
          TX_NUMERO_DEST: registro.TX_NUMERO_DEST || '',
          TX_COMPLEMENTO_DEST: registro.TX_COMPLEMENTO_DEST || '',
          TX_BAIRRO_DEST: registro.TX_BAIRRO_DEST || '',
          TX_CEP_DEST: registro.TX_CEP_DEST || '',
          ID_UF_DEST: registro.ID_UF_DEST || '',
          ID_MUNICIPIO_DEST: registro.ID_MUNICIPIO_DEST || '',
          TX_FONE_DEST: registro.TX_FONE_DEST || '',
          TX_EMAIL_DEST: registro.TX_EMAIL_DEST || '',
          PAIS_DESTINATARIO_ID: registro.PAIS_DESTINATARIO_ID || '1058',
          
          // Totais
          VL_BASE_CALC_ICMS: registro.VL_BASE_CALC_ICMS || 0,
          VL_TOTAL_ICMS: registro.VL_TOTAL_ICMS || 0,
          VL_BASE_CALC_ST_ICMS: registro.VL_BASE_CALC_ST_ICMS || 0,
          VL_TOTAL_ST_ICMS: registro.VL_TOTAL_ST_ICMS || 0,
          VL_TOTAL_PRODUTOS: registro.VL_TOTAL_PRODUTOS || 0,
          VL_TOTAL_FRETE: registro.VL_TOTAL_FRETE || 0,
          VL_TOTAL_SEGURO: registro.VL_TOTAL_SEGURO || 0,
          VL_TOTAL_DESCONTO: registro.VL_TOTAL_DESCONTO || 0,
          VL_TOTAL_II: registro.VL_TOTAL_II || 0,
          VL_TOTAL_IPI: registro.VL_TOTAL_IPI || 0,
          VL_TOTAL_PIS: registro.VL_TOTAL_PIS || 0,
          VL_TOTAL_COFINS: registro.VL_TOTAL_COFINS || 0,
          VL_TOTAL_OUTRAS_DESP: registro.VL_TOTAL_OUTRAS_DESP || 0,
          VL_TOTAL_NFE: registro.VL_TOTAL_NFE || 0,
          
          // Transporte
          OP_MODALIDADE_FRETE: registro.OP_MODALIDADE_FRETE || '1',
          ID_TRANSPORTADOR: registro.ID_TRANSPORTADOR || null,
          TX_RAZAO_SOCIAL_TRANSP: registro.TX_RAZAO_SOCIAL_TRANSP || '',
          TX_NUM_CNPJ_TRANSP: registro.TX_NUM_CNPJ_TRANSP || '',
          TX_NUM_IE_TRANSP: registro.TX_NUM_IE_TRANSP || '',
          TX_PLACA_VEIC_FRETE: registro.TX_PLACA_VEIC_FRETE || '',
          ID_UF_PLACA_VEIC_FRETE: registro.ID_UF_PLACA_VEIC_FRETE || '',
          NR_PESO_BRUTO: registro.NR_PESO_BRUTO || 0,
          NR_PESO_LIQUIDO: registro.NR_PESO_LIQUIDO || 0,
          
          // Cobrança
          TX_FATURA_NUMERO: registro.TX_FATURA_NUMERO || '',
          VL_FATURA_VL_ORIGINAL: registro.VL_FATURA_VL_ORIGINAL || 0,
          VL_FATURA_VL_DESCONTO: registro.VL_FATURA_VL_DESCONTO || 0,
          VL_FATURA_VL_LIQUIDO: registro.VL_FATURA_VL_LIQUIDO || 0,
          OP_FORMA_PAGAMENTO: registro.OP_FORMA_PAGAMENTO || null,
          NR_PARCELAS: registro.NR_PARCELAS || null,
          
          // Informações adicionais
          OB_ADICIONAIS_FISCO: registro.OB_ADICIONAIS_FISCO || '',
          OB_COMPLEMENT_CONTRIBUINTE: registro.OB_COMPLEMENT_CONTRIBUINTE || '',
          OB_COMPLEMENT_CONTRIBUINTE_ORIG: registro.OB_COMPLEMENT_CONTRIBUINTE_ORIG || '',
          OBS_OUTROS: registro.OBS_OUTROS || '',
          
          // Outros
          CFOP_ID: registro.CFOP_ID || '',
          TIPO_MOVIMENTACAO_ID: registro.TIPO_MOVIMENTACAO_ID || null,
          ID_SEQUENCIA_VENDAS: registro.ID_SEQUENCIA_VENDAS || null,
          TX_HORA_ENTRA_SAI: registro.TX_HORA_ENTRA_SAI || '',
          TX_NUM_DIGEST_VALUE: registro.TX_NUM_DIGEST_VALUE || '',
          DT_PROTOCOLO_NFE: registro.DT_PROTOCOLO_NFE ? Tools.formatarDataParaInput(registro.DT_PROTOCOLO_NFE) : '',
          
          // Arrays (podem vir vazios, serão carregados por endpoints separados)
          itens: registro.itens || registro.ITENS || [],
          parcelas: registro.parcelas || registro.DUPLICATAS || [],

          // Reforma Tributária
          VL_IBSCBS_BASE_TOTAL: registro.VL_IBSCBS_BASE_TOTAL || null,
          VL_IBSUF_TOTAL: registro.VL_IBSUF_TOTAL || null,
          VL_IBSMUN_TOTAL: registro.VL_IBSMUN_TOTAL || null,
          VL_CBS_TOTAL: registro.VL_CBS_TOTAL || null,
          OP_REFORMA_TRIBUTARIA: registro.OP_REFORMA_TRIBUTARIA || '0',
          CMUNFGIBS: registro.CMUNFGIBS || null,
          TPNF_DEBITO: registro.TPNF_DEBITO || null,
          TPNF_CREDITO: registro.TPNF_CREDITO || null,
          TP_ENTE_GOV: registro.TP_ENTE_GOV || null,
          PREDUTOR_GOV: registro.PREDUTOR_GOV || null,
          TP_OPER_GOV: registro.TP_OPER_GOV || null,
          VISTOT: registro.VISTOT || null,
          VBCIBSCBSTOT: registro.VBCIBSCBSTOT || null,
          VIBSTOT: registro.VIBSTOT || null,
          VCREDPRES_TOT: registro.VCREDPRES_TOT || null,
          VCREDPRESCONDSUS_TOT: registro.VCREDPRESCONDSUS_TOT || null,
          VDEVTRIB_IBSUF_TOT: registro.VDEVTRIB_IBSUF_TOT || null,
          VDIF_IBSUF_TOT: registro.VDIF_IBSUF_TOT || null,
          VDEVTRIB_IBSMUN_TOT: registro.VDEVTRIB_IBSMUN_TOT || null,
          VDIF_IBSMUN_TOT: registro.VDIF_IBSMUN_TOT || null,
          VCBS_TOT: registro.VCBS_TOT || null,
          VCREDPRES_CBS_TOT: registro.VCREDPRES_CBS_TOT || null,
          VCREDPRESCONDSUS_CBS_TOT: registro.VCREDPRESCONDSUS_CBS_TOT || null,
          VL_CREDITO_PRESUMIDO: registro.VL_CREDITO_PRESUMIDO || null
        })

        // Verificar se o total da nota está zerado (NFe recém-criada sem cálculo correto)
        // Considera zerado se VL_TOTAL_NFE for 0, mesmo que VL_TOTAL_PRODUTOS tenha valor
        const totalNfeZerado = (
          (Number(this.nfe.VL_TOTAL_NFE) || 0) === 0 &&
          (Number(this.nfe.VL_TOTAL_PRODUTOS) || 0) > 0
        )

        // Forçar reatividade nos campos de totais usando $set APÓS Object.assign
        this.$set(this.nfe, 'VL_TOTAL_PRODUTOS', Number(registro.VL_TOTAL_PRODUTOS || 0))
        this.$set(this.nfe, 'VL_TOTAL_ICMS', Number(registro.VL_TOTAL_ICMS || 0))
        this.$set(this.nfe, 'VL_TOTAL_IPI', Number(registro.VL_TOTAL_IPI || 0))
        this.$set(this.nfe, 'VL_TOTAL_PIS', Number(registro.VL_TOTAL_PIS || 0))
        this.$set(this.nfe, 'VL_TOTAL_COFINS', Number(registro.VL_TOTAL_COFINS || 0))
        this.$set(this.nfe, 'VL_TOTAL_FRETE', Number(registro.VL_TOTAL_FRETE || 0))
        this.$set(this.nfe, 'VL_TOTAL_SEGURO', Number(registro.VL_TOTAL_SEGURO || 0))
        this.$set(this.nfe, 'VL_TOTAL_DESCONTO', Number(registro.VL_TOTAL_DESCONTO || 0))
        this.$set(this.nfe, 'VL_TOTAL_NFE', Number(registro.VL_TOTAL_NFE || 0))

        // Aguardar Vue processar as mudanças
        await this.$nextTick()
        
        // Setar o registro selecionado no store para manter destaque na listagem
        if (this.nfe.ID) {
          this.$store.commit('nfe/SET_REGISTRO_SELECIONADO_ID', this.nfe.ID)
          
          // Carregar eventos da NF-e
          await this.carregarEventos(this.nfe.ID)
          await this.carregarItensNfe()
        }
        
        // Forçar recriação dos componentes alterando as keys
        this.totaisKey++
        this.abasKey++
        
        // Pequeno delay para garantir que todos os componentes foram montados/atualizados
        manterLoadingAteMontagem = true
        setTimeout(async () => {
          this.isLoadingFromBackend = false // Desativar flag para permitir edição
          
          // Se total da NFe está zerado mas tem produtos, recarregar
          if (totalNfeZerado) {
            setTimeout(async () => {
              try {
                const nfeAtualizada = await this.$store.dispatch('nfe/GET_BY_ID', this.nfe.ID)
                this.onNfeAtualizada(nfeAtualizada)
              } catch (err) {
                console.error('[NfeCadastro] Erro ao recalcular totais:', err)
              }
            }, 1500) // Aguardar itens serem carregados pela aba de produtos
          }
        }, 100)

      } catch (err) {
        console.error('Erro ao carregar NFe:', err)
        this.$bvToast.toast(err?.message || 'Falha ao carregar NF-e', { variant: 'danger', solid: true })
      } finally {
        if (!manterLoadingAteMontagem) {
          this.isLoadingFromBackend = false
        }
        this.loadingTabs.dados = false
        this.$store.commit('controle/setAguardando', false)
      }
    },

    onNfeAtualizada(nfeAtualizada) {
      const registro = Array.isArray(nfeAtualizada) ? nfeAtualizada[0] : nfeAtualizada
      if (!registro) return

      Object.assign(this.nfe, registro)
      this.totaisKey += 1
      this.abasKey += 1
    },

    async salvar(options = {}) {
      try {
        const silencioso = Boolean(options.silencioso)
        // Determinar se é novo registro ou edição
        const isNovo = !this.nfe.ID || this.nfe.ID === null || this.nfe.ID === ''
        const endpoint = isNovo 
          ? '/api/protected/notaFiscal/post' 
          : '/api/protected/notaFiscal/edit'
        
        // Lista de campos válidos da tabela TB_NFE
        const camposValidosTbNfe = [
          'ID', 'TX_CHAVE_ACESSO', 'OP_STATUS', 'TX_NUM_PROTOCOLO', 'TX_NUM_RECIBO', 'TX_SITUACAO',
          'TX_MODELO', 'ID_SERIE', 'DT_EMISSAO', 'DT_SADIA_ENTRADA', 'OP_NATUREZA_OPERACAO',
          'OP_TIPO_DOCUMENTO', 'OP_TIPO_IMPRESSAO', 'OP_FORMA_PAGAMENTO', 'OP_FORMA_EMISSAO',
          'OP_FINALIDADE_EMISSAO', 'ID_UF_NFE', 'TX_NUM_CNPJ_EMIT', 'TX_RAZAO_SOCIAL_EMIT',
          'TX_NOME_FANTASIA_EMIT', 'TX_NUM_IE_EMIT', 'TX_NUM_IE_SUBST_EMIT', 'TX_NUM_IM_EMIT',
          'TX_CNAE_EMIT', 'TX_LOGRADOURO_EMIT', 'TX_NUMERO_EMIT', 'TX_COMPLEMENTO_EMIT',
          'TX_BAIRRO_EMIT', 'TX_CEP_EMIT', 'TX_PAIS_EMIT', 'ID_UF_EMIT', 'TX_FONE_EMIT',
          'OP_TIPO_DOC_DEST', 'TX_NUM_CNPJ_DEST', 'TX_RAZAO_SOCIAL_DEST', 'TX_NUM_IE_DEST',
          'TX_NUM_SUFRAMA_DEST', 'TX_LOGRADOURO_DEST', 'TX_NUMERO_DEST', 'TX_COMPLEMENTO_DEST',
          'TX_BAIRRO_DEST', 'TX_CEP_DEST', 'TX_PAIS_DEST', 'ID_UF_DEST', 'TX_FONE_DEST',
          'OP_LOCAL_RETIRA_DIF_EMIT', 'TX_NUM_CNPJ_RET', 'TX_LOGRADOURO_RET', 'TX_NUMERO_RET',
          'TX_COMPLEMENTO_RET', 'TX_BAIRRO_RET', 'ID_UF_RET', 'TX_NUM_CNPJ_ENT',
          'TX_LOGRADOURO_ENT', 'TX_NUMERO_ENT', 'TX_COMPLEMENTO_ENT', 'TX_BAIRRO_ENT', 'ID_UF_ENT',
          'VL_BASE_CALC_ICMS', 'VL_TOTAL_ICMS', 'VL_BASE_CALC_ST_ICMS', 'VL_TOTAL_ST_ICMS',
          'VL_TOTAL_PRODUTOS', 'VL_TOTAL_FRETE', 'VL_TOTAL_SEGURO', 'VL_TOTAL_DESCONTO',
          'VL_TOTAL_II', 'VL_TOTAL_IPI', 'VL_TOTAL_PIS', 'VL_TOTAL_COFINS', 'VL_TOTAL_OUTRAS_DESP',
          'VL_TOTAL_TOTAL_NFE', 'VL_BASE_CALC_ISS', 'VL_TOTAL_ISS', 'VL_PIS_SOBRE_ISS',
          'VL_COFINS_SOBRE_ISS', 'VL_TOTAL_SOBRE_NAO_INCID_ISS', 'VL_RETIDO_PIS', 'VL_RETIDO_COFINS',
          'VL_RETIDO_CSLL', 'VL_BASE_CALC_IRRF', 'VL_RETIDO_IRRF', 'VL_BASE_CALC_RET_PREV',
          'VL_TOTAL_RET_PREV', 'OP_MODALIDADE_FRETE', 'ID_TRANSPORTADOR', 'OP_TIPO_DOC_TRANSP',
          'TX_RAZAO_SOCIAL_TRANSP', 'TX_NUM_IE_TRANSP', 'OP_ICMS_ISENTO_TRANSP',
          'TX_LOGRADOURO_TRANSP', 'ID_UF_TRANSP', 'VL_BASE_CALC_ICMS_FRETE', 'VL_ICMS_FRETE',
          'VL_FRETE', 'ID_UF_FRETE', 'ID_CFOP_FRETE', 'TX_PLACA_VEIC_FRETE', 'TX_RNTC_VEIC_FRETE',
          'ID_UF_PLACA_VEIC_FRETE', 'OB_ADICIONAIS_FISCO', 'OB_COMPLEMENT_CONTRIBUINTE', 'OB_COMPLEMENT_CONTRIBUINTE_ORIG',
          'ID_CLIENTE_NFE', 'TX_FATURA_NUMERO', 'VL_FATURA_VL_ORIGINAL', 'VL_FATURA_VL_DESCONTO',
          'VL_FATURA_VL_LIQUIDO', 'TX_NUM_DIGEST_VALUE', 'VL_TOTAL_NFE', 'VL_ICMS_NFE',
          'VL_ICMS_ST_NFE', 'TX_NUM_PROTOCOLO_NFE', 'DT_PROTOCOLO_NFE', 'TX_NUM_CNPJ_TRANSP',
          'ID_MUNICIPIO_EMIT', 'ID_MUNICIPIO_DEST', 'ID_MUNICIPIO_RET', 'ID_MUNICIPIO_ENT',
          'ID_MUNICIPIO_TRANSP', 'ID_MUNICIPIO_OCORRENCIA', 'ID_MUNICIPIO_NFE', 'ID_SEQUENCIA_VENDAS',
          'TX_NUM_SERIE', 'OP_DANFE_IMPRESSO', 'OP_EXPORTADA_XML', 'DT_CADASTRO', 'TX_NATUREZA_OPERACAO',
          'TX_NUM_NFE', 'OP_LOCAL_ENTREGA_DIF_EMIT', 'TX_NUM_LOTE', 'DT_PROTOCOLO', 'HR_PROTOCOLO',
          'DT_RECIBO', 'HR_RECIBO', 'TX_STATUS_RECIBO', 'TX_STATUS_LOTE', 'DT_RECIBO_CANCELAMENTO',
          'TX_NUM_RECIBO_CANCELAMENTO', 'HR_RECIBO_CANCELAMENTO', 'DT_EXCLUSAO', 'HR_EXCLUSAO',
          'TX_USUARIO_EXCLUSAO', 'OP_ICMS_ISENTO_DEST', 'TX_HORA_ENTRA_SAI', 'TX_EMAIL_DEST',
          'OP_REGIME_TRIBUTARIO', 'NR_PESO_BRUTO', 'NR_PESO_LIQUIDO', 'NR_PARCELAS', 'OP_GEROU_BOLETOS',
          'ID_EMPRESA', 'OP_CONSUMIDOR_FINAL', 'OP_DESTINO_OPERACAO', 'OP_TIPO_ATENDIMENTO',
          'UF_EXPORTA_ID', 'TX_LOCEXPORTA', 'TX_LOCDESPACHO', 'PAIS_DESTINATARIO_ID',
          'PERCENTUAL_FUNRURAL', 'VL_FUNRURAL', 'LOTE_COMPRA_GADO', 'CFOP_ID', 'VL_TOTAL_ICMS_DESONERA',
          'OP_PDF_ONLINE', 'OP_TIPO_AMBIENTE', 'NFE_DISTRIBUICAO_ID', 'CHAVE_ACESSO_DISTRIBUICAO',
          'COMPRA_GADO_ID', 'OBS_OUTROS', 'ABATE_ID', 'TX_USUARIO_CADASTRO', 'TX_USUARIO_TRANSMISSAO',
          'TX_USUARIO_CANCELAMENTO', 'OP_NOTA', 'TIPO_MOVIMENTACAO_ID', 'VL_CREDITO_PRESUMIDO',
          'CREDITO_PRESUMIDO_PERC',
          // Reforma Tributária
          'VL_IBSCBS_BASE_TOTAL', 'VL_IBSUF_TOTAL', 'VL_IBSMUN_TOTAL', 'VL_CBS_TOTAL',
          'OP_REFORMA_TRIBUTARIA', 'CMUNFGIBS', 'TPNF_DEBITO', 'TPNF_CREDITO',
          'TP_ENTE_GOV', 'PREDUTOR_GOV', 'TP_OPER_GOV', 'VISTOT', 'VBCIBSCBSTOT',
          'VIBSTOT', 'VCREDPRES_TOT', 'VCREDPRESCONDSUS_TOT', 'VDEVTRIB_IBSUF_TOT',
          'VDIF_IBSUF_TOT', 'VDEVTRIB_IBSMUN_TOT', 'VDIF_IBSMUN_TOT', 'VCBS_TOT',
          'VCREDPRES_CBS_TOT', 'VCREDPRESCONDSUS_CBS_TOT'
        ]
        
        // Filtrar apenas campos válidos da tabela TB_NFE
        const nfeLimpo = {}
        camposValidosTbNfe.forEach(campo => {
          if (this.nfe.hasOwnProperty(campo)) {
            nfeLimpo[campo] = this.nfe[campo]
          }
        })
        
        if (!silencioso) {
          this.$bvToast.toast(isNovo ? 'Salvando nova NF-e...' : 'Atualizando NF-e...', { 
            variant: 'info', 
            solid: true,
            noAutoHide: true,
            id: 'toast-salvando'
          })
        }
        
        // Montar payload com nfe e usuario separados
        const payload = { 
          nfe: nfeLimpo, 
          usuario: window.localStorage.getItem('login') 
        }
        
        const { data } = await this.$http.post(endpoint, payload)
        
        if (!silencioso) {
          this.$bvToast.hide('toast-salvando')
        }
        
        // Atualizar ID se for novo registro
        if (isNovo && data) {
          const registro = Array.isArray(data) ? data[0] : data
          if (registro && registro.ID) {
            this.nfe.ID = registro.ID
            // Redirecionar para modo edição
            this.$router.replace({ name: 'NfeCadastro', params: { id: registro.ID } })
          }
        }
        
        // Recarregar a NF-e completa do backend para atualizar todos os dados
        if (this.nfe.ID) {
          await this.carregarNfe(this.nfe.ID)
        }
        
        if (!silencioso) {
          this.$bvToast.toast('NF-e salva com sucesso!', { variant: 'success', solid: true })
        }
        return true
        
      } catch (err) {
        if (!silencioso) {
          this.$bvToast.hide('toast-salvando')
        }
        const mensagem = err?.response?.data?.message || err?.message || 'Erro ao salvar NF-e'
        this.$bvToast.toast(mensagem, { variant: 'danger', solid: true })
        console.error('Erro ao salvar NF-e:', err)
        return false
      }
    },

    async recalcularNfePorAlteracao(mensagemSucesso) {
      if (this.isLoadingFromBackend) return
      if (!this.nfe.ID) return

      try {
        this.$store.commit('controle/setAguardando', true)
        await this.$nextTick()

        const salvoOk = await this.salvar({ silencioso: true })
        if (!salvoOk) return

        await this.$http.post('/api/protected/notaFiscal/recalcular', {
          nfeId: this.nfe.ID,
          usuario: window.localStorage.getItem('login')
        })

        await this.carregarNfe(this.nfe.ID)
        this.$bvToast.toast(mensagemSucesso, {
          title: 'NF-e',
          variant: 'success',
          solid: true
        })
      } catch (err) {
        const mensagem = err?.response?.data || err?.message || 'Erro ao recalcular impostos da NF-e'
        this.$bvToast.toast(mensagem, { title: 'Aviso', variant: 'danger', solid: true })
      } finally {
        this.$store.commit('controle/setAguardando', false)
      }
    },

    async onTipoMovimentacaoAlterada() {
      await this.recalcularNfePorAlteracao('Itens e impostos recalculados para o novo tipo de movimentacao.')
    },

    async onDestinatarioAlterado() {
      await this.recalcularNfePorAlteracao('Destinatario e impostos recalculados.')
    },

    async transmitir() {
      // Persistir dados da tela antes da transmissão
      

      if (!this.nfe.ID) {
        this.$bvToast.toast('NF-e não possui ID válido para transmissão', { variant: 'warning', solid: true })
        return
      }

      const confirmado = await this.$bvModal.msgBoxConfirm(
        'Deseja transmitir esta NF-e para a SEFAZ?',
        {
          title: 'Confirmar Transmissão',
          okVariant: 'success',
          okTitle: 'Sim',
          centered: true,
          cancelTitle: 'Não'
        }
      )
      
      if (!confirmado) return

      try {
        this.$store.commit('controle/setAguardando', true)

        if (this.podeEditar) {
          const salvo = await this.salvar()
          if (!salvo) {
            this.$store.commit('controle/setAguardando', false)
            return
          }
        }

        const body = {
          nfeId: this.nfe.ID,
          usuario: window.localStorage.getItem('login'),
          tela: 'N'
        }

        const response = await this.$http.post('/api/protected/notaFiscal/processarIntegrador', body)
        const resultado = response.data

        if (!resultado || !resultado.sucesso || !resultado.pdfBase64) {
          const erro = resultado?.erro || 'Não foi possível transmitir a NF-e.'
          this.$bvToast.toast(erro, { title: 'Erro', variant: 'danger', solid: true })
          return
        }

        // Converte base64 para blob
        const byteCharacters = atob(resultado.pdfBase64)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: 'application/pdf' })

        // Cria a URL e abre o PDF
        const url = window.URL.createObjectURL(blob)
        window.open(url)

        // Recarregar a NF-e para atualizar status
        await this.carregarNfe(this.nfe.ID)

      } catch (err) {
        console.error('Erro ao transmitir NF-e:', err)
        
        // Extrair mensagem de erro da mesma forma que Tools.exibirMensagem()
        let mensagem = ''
        try {
          if (err && err.response && err.response.data != null) {
            if (typeof err.response.data === 'string') {
              mensagem = err.response.data
            } else if (err.response.data.erro) {
              mensagem = err.response.data.erro
            } else if (err.response.data.message) {
              mensagem = err.response.data.message
            } else {
              mensagem = JSON.stringify(err.response.data)
            }
          } else if (typeof err === 'string') {
            mensagem = err
          } else if (err && err.message) {
            mensagem = err.message
          } else {
            mensagem = 'Erro desconhecido'
          }
        } catch (e) {
          // ignora erro na extração da mensagem
        }
        
        // Parsear a resposta - se houver linhas separadas, mostrar lista
        const linhas = mensagem.split('\n').filter(l => l.trim().length > 0)
        
        this.erroTransmissao.mensagem = mensagem
        this.erroTransmissao.linhas = linhas
        
        // Recarregar a NF-e mesmo em caso de erro (rejeição da SEFAZ atualiza status e eventos)
        if (this.nfe.ID) {
          await this.carregarNfe(this.nfe.ID)
        }
        
        // Exibir modal customizado
        this.$refs.modalErroTransmissao.show()
      } finally {
        this.$store.commit('controle/setAguardando', false)
      }
    },

    async consultarSefaz() {
      if (this.consultandoStatusServico) return

      try {
        this.consultandoStatusServico = true

        const response = await this.$http.post('/api/protected/integrador/statusServico', {})
        const mensagem = response?.data?.msg || 'Status do serviço da SEFAZ consultado com sucesso.'

        await this.$bvModal.msgBoxOk(mensagem, {
          title: 'Status do Serviço SEFAZ',
          size: 'lg',
          okTitle: 'Fechar',
          centered: true,
          headerClass: 'bg-info text-white',
          bodyClass: 'text-pre-wrap'
        })
      } catch (err) {
        const mensagem = err?.response?.data?.erro || err?.response?.data?.message || (typeof err?.response?.data === 'string' ? err.response.data : null) || err?.message || 'Não foi possível consultar o status do serviço da SEFAZ.'
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', mensagem)
      } finally {
        this.consultandoStatusServico = false
      }
    },

    async imprimirDanfe() {
      if (!this.nfe.ID) {
        this.$bvToast.toast('NF-e não possui ID válido', { variant: 'warning', solid: true })
        return
      }

      try {
        this.$store.commit('controle/setAguardando', true)

        const body = {
          nfeId: this.nfe.ID,
          empresaId: this.nfe.ID_EMPRESA
        }

        const response = await this.$http.post(`/api/protected/notaFiscal/downloadPDF/`, body)
        const resultado = response.data

        if (!resultado || !resultado.sucesso || !resultado.pdfBase64) {
          const erro = resultado?.erro || 'Não foi possível baixar o PDF da Nota Fiscal.'
          this.$bvToast.toast(erro, { title: 'Erro', variant: 'danger', solid: true })
          return
        }

        // Converte base64 para blob
        const byteCharacters = atob(resultado.pdfBase64)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: 'application/pdf' })

        // Cria a URL e abre o PDF
        const url = window.URL.createObjectURL(blob)
        window.open(url)

      } catch (err) {
        console.error('Erro ao imprimir DANFE:', err)
        const mensagem = err?.response?.data?.erro || err?.response?.data?.message || err?.message || 'Erro ao imprimir DANFE'
        this.$bvToast.toast(mensagem, { title: 'Erro', variant: 'danger', solid: true })
      } finally {
        this.$store.commit('controle/setAguardando', false)
      }
    },

    enviarEmail() {
      if (this.$refs['modal-danfe']) {
        this.$refs['modal-danfe'].openFor(this.nfe)
      } else {
        this.$bvToast.toast('Modal de envio não disponível', { variant: 'danger', solid: true })
      }
    },

    onDanfeSent() {},

    async exportarXML() {
      if (!this.nfe.ID) {
        this.$bvToast.toast('NF-e não possui ID válido', { variant: 'warning', solid: true })
        return
      }

      try {
        this.$store.commit('controle/setAguardando', true)

        const payload = {
          nfeId: this.nfe.ID,
          empresaId: this.nfe.ID_EMPRESA || null,
          chaveAcesso: this.nfe.TX_CHAVE_ACESSO || null
        }

        const response = await this.$http.post('/api/protected/notaFiscal/downloadXML/', payload)
        const resultado = response.data

        if (!resultado || !resultado.sucesso || !resultado.xmlBase64) {
          const erro = resultado?.erro || 'Não foi possível exportar o XML da NF-e.'
          this.$bvToast.toast(erro, { title: 'Erro', variant: 'danger', solid: true })
          return
        }

        const byteCharacters = atob(resultado.xmlBase64)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }

        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: 'application/xml;charset=utf-8' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = resultado.fileName || `${this.nfe.TX_CHAVE_ACESSO || `nfe-${this.nfe.ID}`}-nfe.xml`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (err) {
        console.error('Erro ao exportar XML da NF-e:', err)
        const mensagem = err?.response?.data?.erro || err?.response?.data?.message || (typeof err?.response?.data === 'string' ? err.response.data : null) || err?.message || 'Erro ao exportar XML da NF-e'
        this.$bvToast.toast(mensagem, { title: 'Erro', variant: 'danger', solid: true })
      } finally {
        this.$store.commit('controle/setAguardando', false)
      }
    },

    cancelar() {
      // TODO: cancelamento via backend/SEFAZ
      this.$bvModal.msgBoxConfirm('Deseja cancelar esta NF-e?').then(ok => {
        if (ok) this.$bvToast.toast('Cancelar (TODO)', { variant: 'danger', solid: true })
      })
    },

    cartaCorrecao() {
      // TODO: carta de correção flow
    },

    async carregarEventos(nfeId) {
      try {
        this.loadingEventos = true
        const { data } = await this.$http.get(`/api/protected/notaFiscal/evento/getByNfeId/${nfeId}`)
        this.eventos = Array.isArray(data) ? data : []
      } catch (err) {
        console.error('Erro ao carregar eventos:', err)
        this.$bvToast.toast('Erro ao carregar eventos da NF-e', { variant: 'warning', solid: true })
        this.eventos = []
      } finally {
        this.loadingEventos = false
      }
    },

    abrirModalImpostos() {
      this.$refs['modal-impostos'].show()
    },

    getEventoTipoTexto(tipo) {
      const tipoMap = {
        'E': 'Enviado',
        'A': 'Autorizado',
        'C': 'Cancelado',
        'T': 'Carta de Correção',
        'R': 'Rejeitada'
      }
      return tipoMap[tipo] || tipo
    },

    getEventoTipoBadge(tipo) {
      const badgeMap = {
        'E': 'badge badge-info',
        'A': 'badge badge-success',
        'C': 'badge badge-danger',
        'T': 'badge badge-warning',
        'R': 'badge badge-danger'
      }
      return badgeMap[tipo] || 'badge badge-secondary'
    },

    formatarData(data) {
      if (!data) return ''
      try {
        return new Date(data).toLocaleString('pt-BR')
      } catch {
        return data
      }
    },

    async imprimirCartaCorrecao(eventoId) {
      try {
        this.$store.commit('controle/setAguardando', true)

        const response = await this.$http.get(`/api/protected/integrador/downloadCartaCorrecao/${this.nfe.ID}/${eventoId}`)
        
        const resultado = response.data

        if (resultado && resultado.pdfBase64) {
          // Converte base64 para blob
          const byteCharacters = atob(resultado.pdfBase64)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], { type: 'application/pdf' })

          // Cria a URL e abre o PDF
          const url = window.URL.createObjectURL(blob)
          window.open(url)
        } else {
          Tools.exibirMensagem(this.$store, 'Aviso', 'warning', 'PDF não disponível')
        }

      } catch (err) {
        const erro = err?.response?.data?.erro || err?.response?.data?.mensagem || 'Erro ao baixar a Carta de Correção'
        console.log(erro)
        Tools.exibirMensagem(this.$store, 'Aviso', 'warning', erro)
      } finally {
        this.$store.commit('controle/setAguardando', false)
      }
    },

    async verDetalhesEvento(evento) {
      try {
        // Buscar narrativa completa do evento (não vem na listagem)
        const { data } = await this.$http.get(`/api/protected/notaFiscal/evento/getById/${evento.ID}`)
        
        let narrativa = (Array.isArray(data) ? data[0]?.NARRATIVA : data?.NARRATIVA) || ''
        
        // Remover mensagem de falha se existir
        narrativa = narrativa.replace(/FALHA!!!! O Protocolo não foi retornado, por favor informe ao administrador do sistema!/gi, '')
        
        // Formatar XML
        if (narrativa.length > 0) {
          // Adicionar declaração XML se não existir
          if (!narrativa.includes('<?xml')) {
            narrativa = '<?xml version="1.0" encoding="utf-8"?>' + narrativa
          }
          this.eventoDetalhes.formatted = this.formatXml(narrativa)
        } else {
          this.eventoDetalhes.formatted = '<Info>Mensagem Inexistente</Info>'
        }
        
        this.eventoDetalhes.xml = narrativa
        this.$refs.modalDetalhesEvento.show()

      } catch (err) {
        console.error('Erro ao carregar detalhes do evento:', err)
        this.$bvToast.toast('Erro ao carregar detalhes do evento', { variant: 'danger', solid: true })
      }
    },

    formatXml(xml) {
      // Formatar XML para melhor legibilidade
      try {
        const PADDING = '  '
        const reg = /(>)(<)(\/*)/g
        let formatted = xml.replace(reg, '$1\r\n$2$3')
        let pad = 0
        
        formatted = formatted.split('\r\n').map(node => {
          let indent = 0
          if (node.match(/.+<\/\w[^>]*>$/)) {
            indent = 0
          } else if (node.match(/^<\/\w/)) {
            if (pad !== 0) {
              pad -= 1
            }
          } else if (node.match(/^<\w[^>]*[^\/]>.*$/)) {
            indent = 1
          } else {
            indent = 0
          }
          
          const padding = PADDING.repeat(pad)
          pad += indent
          
          return padding + node
        }).join('\r\n')
        
        return formatted
      } catch {
        return xml
      }
    },

    abrirModalCartaCorrecaoAba() {
      if (this.$refs['modal-carta-correcao']) {
        this.$refs['modal-carta-correcao'].abrir(this.nfe)
      } else {
        this.$bvToast.toast('Modal não disponível', { title: 'Erro', variant: 'danger' })
      }
    },

    async onCartaEnviada() {
      // Recarregar eventos após envio da carta
      if (this.nfe.ID) {
        await this.carregarEventos(this.nfe.ID)
        this.$bvToast.toast('Lista de eventos atualizada', { 
          title: 'Sucesso', 
          variant: 'success' 
        })
      }
    },

    abrirModalCancelarAba() {
      if (this.$refs['modal-cancelar-nfe']) {
        this.$refs['modal-cancelar-nfe'].abrir(this.nfe)
      } else {
        this.$bvToast.toast('Modal não disponível', { title: 'Erro', variant: 'danger' })
      }
    },

    async onNfeCancelada() {
      // Recarregar NFe e eventos após cancelamento
      if (this.nfe.ID) {
        await this.carregarNfe(this.nfe.ID)
        await this.carregarEventos(this.nfe.ID)
        this.$bvToast.toast('NF-e cancelada e dados atualizados', { 
          title: 'Sucesso', 
          variant: 'success' 
        })
      }
    },

    voltar() {
      // Marcar que a lista precisa ser recarregada quando voltar
      this.$store.commit('nfe/SET_DEVE_RECARREGAR_LISTA', true)
      this.$router.push({ path: '/faturamento/nfe/index' })
    },

    async onNfeAtualizada(nfeAtualizada) {
      // Atualizar dados da NF-e após operações de CRUD nos itens
      // O backend recalcula impostos, totais e volumes
      if (nfeAtualizada || this.nfe.ID) {
        let registro = Array.isArray(nfeAtualizada) ? nfeAtualizada[0] : nfeAtualizada

        if (!registro && this.nfe.ID) {
          const recarregada = await this.$store.dispatch('nfe/GET_BY_ID', this.nfe.ID)
          registro = Array.isArray(recarregada) ? recarregada[0] : recarregada
        }

        if (!registro) return
        
        // Substituir o objeto nfe completamente para garantir reatividade
        this.nfe = Object.assign({}, this.nfe, {
          ID: registro.ID || this.nfe.ID,
          VL_BASE_CALC_ICMS: registro.VL_BASE_CALC_ICMS || 0,
          VL_TOTAL_ICMS: registro.VL_TOTAL_ICMS || 0,
          VL_BASE_CALC_ST_ICMS: registro.VL_BASE_CALC_ST_ICMS || 0,
          VL_TOTAL_ST_ICMS: registro.VL_TOTAL_ST_ICMS || 0,
          VL_TOTAL_PRODUTOS: registro.VL_TOTAL_PRODUTOS || 0,
          VL_TOTAL_FRETE: registro.VL_TOTAL_FRETE || 0,
          VL_TOTAL_SEGURO: registro.VL_TOTAL_SEGURO || 0,
          VL_TOTAL_DESCONTO: registro.VL_TOTAL_DESCONTO || 0,
          VL_TOTAL_II: registro.VL_TOTAL_II || 0,
          VL_TOTAL_IPI: registro.VL_TOTAL_IPI || 0,
          VL_TOTAL_PIS: registro.VL_TOTAL_PIS || 0,
          VL_TOTAL_COFINS: registro.VL_TOTAL_COFINS || 0,
          VL_TOTAL_OUTRAS_DESP: registro.VL_TOTAL_OUTRAS_DESP || 0,
          VL_TOTAL_NFE: registro.VL_TOTAL_NFE || 0,
          NR_PESO_LIQUIDO: registro.NR_PESO_LIQUIDO || 0,
          NR_PESO_BRUTO: registro.NR_PESO_BRUTO || 0,
          // Reforma Tributária - Todos os campos
          VL_IBSCBS_BASE_TOTAL: registro.VL_IBSCBS_BASE_TOTAL || null,
          VL_IBSUF_TOTAL: registro.VL_IBSUF_TOTAL || null,
          VL_IBSMUN_TOTAL: registro.VL_IBSMUN_TOTAL || null,
          VL_CBS_TOTAL: registro.VL_CBS_TOTAL || null,
          OP_REFORMA_TRIBUTARIA: registro.OP_REFORMA_TRIBUTARIA || '0',
          CMUNFGIBS: registro.CMUNFGIBS || null,
          TPNF_DEBITO: registro.TPNF_DEBITO || null,
          TPNF_CREDITO: registro.TPNF_CREDITO || null,
          TP_ENTE_GOV: registro.TP_ENTE_GOV || null,
          PREDUTOR_GOV: registro.PREDUTOR_GOV || null,
          TP_OPER_GOV: registro.TP_OPER_GOV || null,
          VISTOT: registro.VISTOT || null,
          VBCIBSCBSTOT: registro.VBCIBSCBSTOT || null,
          VIBSTOT: registro.VIBSTOT || null,
          VCREDPRES_TOT: registro.VCREDPRES_TOT || null,
          VCREDPRESCONDSUS_TOT: registro.VCREDPRESCONDSUS_TOT || null,
          VDEVTRIB_IBSUF_TOT: registro.VDEVTRIB_IBSUF_TOT || null,
          VDIF_IBSUF_TOT: registro.VDIF_IBSUF_TOT || null,
          VDEVTRIB_IBSMUN_TOT: registro.VDEVTRIB_IBSMUN_TOT || null,
          VDIF_IBSMUN_TOT: registro.VDIF_IBSMUN_TOT || null,
          VCBS_TOT: registro.VCBS_TOT || null,
          VCREDPRES_CBS_TOT: registro.VCREDPRES_CBS_TOT || null,
          VCREDPRESCONDSUS_CBS_TOT: registro.VCREDPRESCONDSUS_CBS_TOT || null,
          VL_CREDITO_PRESUMIDO: registro.VL_CREDITO_PRESUMIDO || null,
          PAIS_DESTINATARIO_ID: registro.PAIS_DESTINATARIO_ID || registro.PAIS_ID || '1058'
        })

        this.$set(this.nfe, 'VL_TOTAL_PRODUTOS', Number(registro.VL_TOTAL_PRODUTOS || 0))
        this.$set(this.nfe, 'VL_TOTAL_ICMS', Number(registro.VL_TOTAL_ICMS || 0))
        this.$set(this.nfe, 'VL_TOTAL_ST_ICMS', Number(registro.VL_TOTAL_ST_ICMS || 0))
        this.$set(this.nfe, 'VL_TOTAL_IPI', Number(registro.VL_TOTAL_IPI || 0))
        this.$set(this.nfe, 'VL_TOTAL_PIS', Number(registro.VL_TOTAL_PIS || 0))
        this.$set(this.nfe, 'VL_TOTAL_COFINS', Number(registro.VL_TOTAL_COFINS || 0))
        this.$set(this.nfe, 'VISTOT', Number(registro.VISTOT || 0))
        this.$set(this.nfe, 'VIBSTOT', Number(registro.VIBSTOT || 0))
        this.$set(this.nfe, 'VCBS_TOT', Number(registro.VCBS_TOT || 0))
        this.$set(this.nfe, 'VL_TOTAL_NFE', Number(registro.VL_TOTAL_NFE || 0))

        await this.$nextTick()
        await this.carregarItensNfe()
        this.totaisKey++
      }
    }
  }
}
</script>

<style scoped>
.page-shell {
  background-color: #f4f7f9;
  min-height: 100vh;
}

.shadow-xs {
  box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important;
}

.rounded-lg {
  border-radius: 1rem !important;
}

.transition-hover {
  transition: all 0.3s ease;
}

.transition-hover:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 15px rgba(0,0,0,0.1) !important;
}

.bg-gradient-primary {
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%) !important;
}

.bg-gradient-success {
  background: linear-gradient(135deg, #28a745 0%, #218838 100%) !important;
}

.bg-gradient-danger {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%) !important;
}

.bg-gradient-warning {
  background: linear-gradient(135deg, #ffc107 0%, #e0a800 100%) !important;
}

.bg-gradient-info {
  background: linear-gradient(135deg, #17a2b8 0%, #117a8b 100%) !important;
}

.btn-glass {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white !important;
  font-weight: 600;
  padding: 0.5rem 1.25rem;
  border-radius: 50px;
  transition: all 0.3s ease;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
}

.btn-glass:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.btn-glass-success:hover { background: rgba(40, 167, 69, 0.8); }
.btn-glass-info:hover { background: rgba(23, 162, 184, 0.8); }
.btn-glass-primary:hover { background: rgba(0, 123, 255, 0.8); }
.btn-glass-danger:hover { background: rgba(220, 53, 69, 0.8); }
.btn-glass-secondary:hover { background: rgba(108, 117, 125, 0.8); }

.btn-glass-dropdown >>> .dropdown-toggle {
  padding: 0 !important;
  border: 0 !important;
  background: transparent !important;
}

.modal-header-premium {
  border-bottom: 0;
  position: relative;
}

.modal-label-premium {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
  margin-bottom: 0.4rem;
  display: block;
}

.opacity-75 {
  opacity: 0.75;
}

.table-premium thead th {
  background-color: #f8f9fa;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  font-weight: 700;
  border-top: 0;
  border-bottom: 2px solid #e9ecef;
  color: #495057;
  padding: 1rem 0.75rem;
}

.table-premium tbody td {
  padding: 1rem 0.75rem;
  vertical-align: middle;
  border-bottom: 1px solid #f1f3f5;
}

.table-premium tbody tr:hover {
  background-color: rgba(74, 144, 226, 0.03);
}

.badge-pill {
  border-radius: 50px;
  padding: 0.4em 0.8em;
}

/* Premium Tabs Styling */
.nav-pills-premium {
  background: #fff;
  padding: 0.5rem;
  border-radius: 50px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  display: inline-flex;
}

.nav-pills-premium .nav-link {
  border-radius: 50px !important;
  padding: 0.6rem 1.5rem !important;
  font-weight: 600 !important;
  color: #6c757d !important;
  transition: all 0.3s ease !important;
  border: none !important;
  margin: 0 2px !important;
}

.nav-pills-premium .nav-link.active {
  background: linear-gradient(45deg, #4a90e2, #357abd) !important;
  color: #fff !important;
  box-shadow: 0 4px 15px rgba(74, 144, 226, 0.3) !important;
}

.nav-pills-premium .nav-link:hover:not(.active) {
  background: #f8f9fa !important;
  color: #4a90e2 !important;
}

.premium-tab-content {
  animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Summary Bar Styling */
.nfe-cadastro {
  .cec-header__icon {
    background: rgba(255, 255, 255, 0.15) !important;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    
    &::before {
      display: block !important;
    }
  }
}

.summary-bar {
  background: #fff;
  border: 1px solid #e9ecef;
}

.bg-soft-primary { background-color: rgba(0, 123, 255, 0.08) !important; }
.bg-soft-info { background-color: rgba(23, 162, 184, 0.08) !important; }
.bg-soft-warning { background-color: rgba(255, 193, 7, 0.08) !important; }
.bg-soft-danger { background-color: rgba(220, 53, 69, 0.08) !important; }

.letter-spacing-1 {
  letter-spacing: 1px;
}

.stat-item small {
  font-size: 0.65rem;
  letter-spacing: 1px;
}

.bg-light-danger {
  background-color: rgba(220, 53, 69, 0.1);
}

.premium-table {
  border-collapse: separate;
  border-spacing: 0 8px;
}

.premium-table thead th {
  border: none;
  color: #6c757d;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 1rem;
}

.premium-table tbody tr {
  background: #fff !important;
  box-shadow: 0 2px 5px rgba(0,0,0,0.02);
  transition: all 0.2s;
}

.premium-table tbody tr:hover {
  transform: scale(1.005);
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
}

.premium-table tbody td {
  border: none;
  padding: 1.2rem 1rem;
  vertical-align: middle;
}

.premium-table tbody tr td:first-child { border-radius: 10px 0 0 10px; }
.premium-table tbody tr td:last-child { border-radius: 0 10px 10px 0; }

.btn-icon {
  width: 32px;
  height: 32px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.variant-soft-info {
  background-color: rgba(23, 162, 184, 0.1);
  color: #17a2b8;
  border: none;
}

.variant-soft-secondary {
  background-color: rgba(108, 117, 125, 0.1);
  color: #6c757d;
  border: none;
}
</style>



// WEBPACK FOOTER //
// src/views/faturamento/nfe/NfeCadastro.vue